// JQL-style search parser for the dashboard quick-search input.
//
// Recognised syntax (inspired by Jira JQL, deliberately small so the
// free-text path stays the default for non-power users):
//
//   id=069e22da-...                  -- exact UUID lookup on document id
//   invoice_id=INV-2026-03-00284     -- exact match on a known field
//   purchase_order:"PO 42"           -- phrase match (quoted)
//   total_amount > 5000              -- numeric range (requires Fulltext)
//   invoice_due_date >= 2026-04-21   -- date range (requires Fulltext)
//
// Anything that doesn't parse as a predicate stays in the `keyword`
// string, which still works as the existing substring search. That
// keeps the UI forgiving: a user typing "invoice_id INV-42" without
// the equals sign still gets results.

export type SearchOperator =
  | '='
  | '!='
  | '>'
  | '<'
  | '>='
  | '<='
  | ':'
  | 'between';
export type LogicalConnector = 'AND' | 'OR';

export interface ParsedPredicate {
  field: string;
  operator: SearchOperator;
  value: string;
  // Logical connector to the PREVIOUS predicate (undefined for the first).
  // Defaults to AND when two predicates appear without an explicit keyword.
  connector?: LogicalConnector;
  // True when the value was written with surrounding quotes (`field="value"`).
  // String fields: the backend switches `=` from case-insensitive STARTS-WITH
  // to STARTS-WITH-OR-ENDS-WITH. Preserved through the chip editor so the
  // quoted intent survives a round-trip.
  quoted?: boolean;
}

export interface ParsedSearch {
  // Structured field=value predicates extracted from the input.
  predicates: ParsedPredicate[];
  // Residual keyword (tokens that didn't match a predicate).
  keyword: string;
  // True when at least one predicate requires the Fulltext module
  // (range operators or an extracted-only field).
  requiresFulltext: boolean;
  // True when the user prefixed the query with "vector:" (opt-in
  // kNN semantic search). Requires the Vector module. Mutually
  // exclusive with `isAiMode` — a query has at most one prefix.
  isVectorMode: boolean;
  // True when the user prefixed the query with "ai:" (opt-in natural
  // language → structured query via the Ollama LLM in fulltextsearch).
  // Routes through fulltextsearch's ai_query_parser, NOT through kNN.
  // Requires fulltext to be enabled on the org.
  isAiMode: boolean;
  // True when the user prefixed the query with "ocr:" (explicit fulltext
  // search over OCR/table/text-companion content). Requires fulltext and must
  // not be routed through vector or AI.
  isOcrMode: boolean;
  // Optional inline sort directive — produced by the `... sort <field>
  // asc|desc` suffix. quick-search.vue applies these to the
  // dashboard's order_by_field/order_by_direction when present.
  sortOverride?: {
    field: string;
    direction: 'asc' | 'desc';
  };
  // Optional inline group-by directive — produced by the
  // `group by <field> [day|week|month]` suffix. quick-search.vue
  // forwards this to dashboard.vue's `selectedGroupBy` state so the
  // table renders backend-aggregated bucket rows. Mirrors the
  // sortOverride routing — directive lives outside the predicate stream.
  groupByOverride?: {
    field: string;
    bucket?: 'day' | 'week' | 'month';
  };
  // Per-chip category overrides keyed by field name. Populated only
  // by the chip-editor host (QuickSearchBox) — the raw-text parser
  // can't infer this because the section the user picked from is a
  // UI-only choice not visible in the serialised query string.
  //
  // Drives wire routing in `computeQuickSearchSlots`: a field like
  // `supplier_name` may appear in BOTH the EXTENDED and the FULLTEXT
  // dropdown sections. When the user picks from FULLTEXT, the chip
  // carries `category='fulltext'` — and the dashboard MUST send
  // `is_fulltext=true` so the request routes to OpenSearch instead
  // of the standard PG path (which would 422 because supplier_name
  // is not a column on `documents`).
  //
  // Lookup precedence: chip override > FIELD_CATALOG default > undefined.
  chipCategoryOverrides?: Map<
    string,
    'standard' | 'extended' | 'fulltext' | 'vector' | 'ocr' | 'ai'
  >;
}

// Fields the user can reference directly. Anything not on this list is
// treated as a literal keyword so typos like `foo=bar` aren't silently
// sent to the backend as a structured query.
export const SUPPORTED_FIELDS: ReadonlySet<string> = new Set([
  'id',
  'filename',
  'invoice_id',
  'invoice_number',
  'purchase_order',
  'vendor_id',
  'supplier_name',
  // Silent alias for `supplier_name` — same column, two business
  // terms. Users coming from procurement systems type `vendor_name`,
  // accountants type `supplier_name`. Backend resolves both to the
  // same OpenSearch field via _AST_FIELD_ALIASES.
  'vendor_name',
  'status',
  'doc_type',
  'invoice_sub_type',
  'ap_assignment_code',
  'invoice_due_date',
  // Bug-23 (2026-04-23): silent alias for `invoice_due_date` — the
  // extraction service writes the date under `due_date` while every
  // doc has `invoice_due_date` empty. The autocomplete keeps showing
  // only the friendly `invoice_due_date` name (FIELD_CATALOG below),
  // but raw queries / saved filters / pasted URLs that use `due_date`
  // are accepted here so they reach the backend instead of being
  // silently dropped into the keyword bucket. The backend resolves
  // both names to the same indexed path (fulltextsearch executer.py
  // _AST_FIELD_ALIASES).
  'due_date',
  'discount_due_date',
  'total_amount',
  'net_amount',
  'tax_amount',
  'invoice_date',
  'terms_net_due_date',
  'terms_discount_date',
  'discount_term',
  'payment_terms',
  'payment_term_description',
  'currency',
  // Standard DB columns — range-filterable on the Postgres side.
  // Aliases (imported_on, updated) are normalised to the canonical
  // column name below so the backend sees a single `created_on` /
  // `last_modified_on` predicate regardless of what the user typed.
  'created_on',
  'imported_on',
  'last_modified_on',
  'updated',
  'updated_on',
  // Additional standard columns that users may filter on. The backend
  // understands = and (for numeric columns) range operators.
  'pages',
  'assigned_to',
  'priority',
  'created_by',
  'last_saved_by',
  'sub_org_name',
  'erp_status',
  'is_edoc',
  // PO match outcome — standard documents.po_match_status column. Accepts
  // the raw statuses and the synthetic `under_matched` / `over_matched`
  // bucket tokens (backend expands the latter to an IN(...)).
  'po_match_status',
]);

// Aliases mapped to the canonical column name. Applied ONLY at the
// backend-serialisation edge (`canonicaliseForBackend`), never in the
// parsed predicate list or the chip editor. Keeps `imported_on=today()`
// visible exactly as the user typed it while the HTTP request still
// carries `created_on=2026-04-22`.
export const FIELD_ALIASES: Record<string, string> = {
  imported_on: 'created_on',
  updated: 'last_modified_on',
  updated_on: 'last_modified_on',
  // Procurement vocabulary — the underlying ClickHouse dashboard
  // column is `supplier_name` (constants/clickhouse.py). Frontend
  // accepts the procurement term `vendor_name` for both raw_query
  // predicates and group-by directives, and rewrites it to the
  // canonical column name BEFORE shipping to the backend so the
  // GROUP BY / WHERE clause references a real column.
  vendor_name: 'supplier_name',
  // Bug-2026-05-29: the backend `/document_process/search/fields` API
  // surfaces `file_name` as a separate OpenSearch custom-field entry
  // alongside the standard `filename` column — both labelled
  // "Dokumentname" via _CUSTOM_FIELD_LABEL_KEY_OVERRIDES. Picking the
  // fulltext-section variant in the dropdown produces a chip with
  // `field='file_name'`, but the actual OpenSearch index path is
  // `metadata.extracted_fields.filename` (NOT `file_name`), so the
  // query returns zero results. Rewrite at the wire boundary so the
  // chip keeps `category='fulltext'` (→ `is_fulltext=true` routing
  // stays correct) while the field name lands on the indexed path.
  // Backend follow-up: add `('file_name', 'filename')` to
  // `_AST_FIELD_ALIAS_GROUPS` in
  // fulltextsearch/routers/opensearch/executer.py so other clients
  // (DocFlow, OpenAPI consumers) get the alias too.
  file_name: 'filename',
};

// Field metadata used by the UI autocomplete. Three categories drive
// the colour in the search input:
//   - standard: blue  (fast Postgres path — always available)
//   - fulltext: orange (needs OpenSearch — range or extracted-only)
//   - vector:   purple (needs Vector module — semantic prefix)
//   - ocr:      green (needs Fulltext module — OCR/table content prefix)
// The `ops` list is only for the tooltip / hint — the parser accepts
// everything and the backend does the real validation.
export type ValueType = 'numeric' | 'date' | 'enum' | 'uuid' | 'string';

export interface FieldMeta {
  name: string;
  // 'standard' = direct documents-table column (fast PG path)
  // 'extended' = projected from document_extracted_data EAV onto a typed
  //              ClickHouse column via SYNC_COLUMNS — filterable as SQL
  //              column without OpenSearch round-trip.
  // 'fulltext' = needs OpenSearch (custom doc-type fields, range ops on
  //              non-projected fields).
  // 'vector'   = semantic-similarity prefix (vector:). kNN over document
  //              embeddings — pure cosine-similarity match.
  // 'ai'       = LLM-based natural-language query parser (ai:). The
  //              backend's Ollama service rewrites the phrase into a
  //              structured query. NOT the same as vector kNN; routed
  //              through fulltextsearch's ai_query_parser, not the
  //              vector index.
  // 'ocr'      = explicit OCR/table/text-companion prefix (ocr:).
  category: 'standard' | 'extended' | 'fulltext' | 'vector' | 'ocr' | 'ai';
  ops: readonly string[]; // typical operators for this field
  // Drives smart-commit rules in QuickSearchBox — numeric/date/enum/uuid
  // values commit on space; string values wait for Enter/Tab so multi-
  // word strings aren't prematurely cut.
  valueType: ValueType;
  // Human-friendly label shown in the autocomplete dropdown. Overrides
  // the automatic Title-Case of `name`. Use this whenever the raw
  // identifier (invoice_id, ap_assignment_code) misleads the user
  // about what the field means (it's Invoice Number, not Invoice Id).
  // When omitted, FieldAutocomplete falls back to prettyFieldName(name).
  displayName?: string;
  // English description used as fallback / for parser tests / documentation.
  description: string;
  // i18n key resolved in the Vue layer via $t(). Keeping the English
  // fallback here lets non-Vue consumers (unit tests, tooling) read the
  // catalog without pulling in Tolgee.
  descriptionKey: string;
  // Short i18n key used as the chip label — one or two words, never a
  // sentence. The dropdown keeps descriptionKey (longer, descriptive);
  // chips use labelKey so rows stay compact. Falls back to descriptionKey
  // when omitted.
  labelKey?: string;
  example: string;
  // When true, the operator picker offers "is not empty" / "is empty"
  // presets that emit the complete `field!=""` / `field=""` predicate in
  // one click. Default false. Useful for fields that may be unset (most
  // extracted fulltext fields, plus a handful of standard columns).
  supportsPresence?: boolean;
  docTypes?: string[];
}

// MUTABLE on purpose: hydrated from the `extended_field_names` payload
// of `GET /document/search/fields` so custom doc-type fields tagged
// dynamically per org get the correct routing class. Empty fallback
// keeps the function pure during unit tests / pre-hydration boot —
// custom fields default to 'fulltext' until the catalog hydrator
// populates this set.
let _EXTENDED_FIELD_NAMES: ReadonlySet<string> = new Set();
let _CUSTOM_FIELD_NAMES: ReadonlySet<string> = new Set();
// Subset of `_CUSTOM_FIELD_NAMES` whose value is free-form text (custom
// doc-type fields that are NOT numeric/date). Used by
// `isMultiTokenStringField` so a pasted multi-word value for a custom
// string field is auto-quoted like `supplier_name` (OBO-46 / Codex review).
let _CUSTOM_STRING_FIELD_NAMES: ReadonlySet<string> = new Set();
// Subset of `_CUSTOM_FIELD_NAMES` whose value is a DATE (custom doc-type
// fields tagged `field_type === 'DATE'`). These never reach the static
// FIELD_CATALOG, so `isDateValueField` consults this set to let the
// dashboard format them with the user's date pattern (DOCB-12248).
let _CUSTOM_DATE_FIELD_NAMES: ReadonlySet<string> = new Set();

export function setExtendedFieldNames(
  names: ReadonlySet<string> | string[],
): void {
  _EXTENDED_FIELD_NAMES = names instanceof Set ? names : new Set(names);
}

export function getExtendedFieldNames(): ReadonlySet<string> {
  return _EXTENDED_FIELD_NAMES;
}

export function setCustomFieldNames(
  names: ReadonlySet<string> | string[],
): void {
  const values = Array.from(names);
  _CUSTOM_FIELD_NAMES = new Set(values.map((name) => name.toLowerCase()));
}

export function getCustomFieldNames(): ReadonlySet<string> {
  return _CUSTOM_FIELD_NAMES;
}

function isSupportedSearchField(fieldName: string): boolean {
  return (
    SUPPORTED_FIELDS.has(fieldName) ||
    _CUSTOM_FIELD_NAMES.has(fieldName) ||
    FIELD_CATALOG.some((field) => field.name === fieldName)
  );
}

// Bug-2026-05-25 #8: custom fulltext fields rendered their raw
// backend `field_name` (e.g. `file_name`) as the dropdown label
// because no `labelKey` was set on the FieldMeta.  When the backend
// field name conceptually overlaps with a standard FIELD_CATALOG
// entry, reuse that entry's `labelKey` so both panels show the same
// human label (e.g. "Document name" for both `filename` standard and
// `file_name` fulltext).  Keep this map small — it's NOT a generic
// alias system; it's a UX fix for fields that exist in both worlds.
const _CUSTOM_FIELD_LABEL_KEY_OVERRIDES: Record<string, string> = {
  // OpenSearch property `file_name` mirrors Postgres column `filename`.
  // Both surface to the user as "Document name" — see en.json key
  // `filename`.
  file_name: 'filename',
};

export function customFieldsToFieldMetas(
  raw:
    | {
        field_name: string;
        title: string;
        doc_types: string[];
        field_type?: string;
      }[]
    | null
    | undefined,
): FieldMeta[] {
  // When the Pinia getAllUniqueFieldsWithDocTypes getter throws (e.g. during
  // store hydration), Vue caches undefined as the computed value. Callers
  // then pass that undefined here, crashing on raw.map(). Return [] so the
  // cascade (effectiveCustomFulltextFields → groups()) stays clean.
  const fields = Array.isArray(raw) ? raw : [];
  const knownNames = new Set(FIELD_CATALOG.map((f) => f.name));
  setCustomFieldNames(
    fields
      .map((field) => field?.field_name)
      .filter(
        (name): name is string => typeof name === 'string' && name.length > 0,
      ),
  );
  const metas = fields
    .filter((c) => !!c?.field_name && !!c?.title)
    .filter((c) => !knownNames.has(c.field_name))
    .map((c) => {
      const fieldType = String(c.field_type ?? '').toUpperCase();
      const isNumeric = ['AMOUNT', 'NUMBER', 'CURRENCY', 'CHARGES'].includes(
        fieldType,
      );
      const isDate = fieldType === 'DATE';
      // Resolve a labelKey for fields whose backend name overlaps with
      // a standard FIELD_CATALOG entry's i18n key.  Falls through to
      // the API-provided title for anything else (the existing
      // behaviour for genuine custom doc-type fields).
      const labelKey = _CUSTOM_FIELD_LABEL_KEY_OVERRIDES[c.field_name];
      return {
        name: c.field_name,
        // Promote to 'extended' (teal) when the backend tells us this
        // field name is in SYNC_COLUMNS — it's then ClickHouse-projected
        // and SQL-filterable. Otherwise stays 'fulltext' (orange).
        category: (_EXTENDED_FIELD_NAMES.has(c.field_name)
          ? 'extended'
          : 'fulltext') as 'extended' | 'fulltext',
        ops: isNumeric
          ? ['=', '!=', '>', '<', '>=', '<=']
          : isDate
            ? ['between', '>=', '<=']
            : ['=', ':', '!='],
        valueType: isNumeric
          ? ('numeric' as const)
          : isDate
            ? ('date' as const)
            : ('string' as const),
        ...(labelKey ? { labelKey } : {}),
        displayName: c.title,
        description: c.title,
        descriptionKey: '',
        example: '',
        supportsPresence: true,
        docTypes: Array.isArray(c.doc_types) ? c.doc_types : [],
      };
    });
  // Remember which custom fields are free-form text so paste-quoting can
  // treat them like `supplier_name` (Codex review).
  _CUSTOM_STRING_FIELD_NAMES = new Set(
    metas
      .filter((m) => m.valueType === 'string')
      .map((m) => m.name.toLowerCase()),
  );
  // Remember which custom fields are dates so the dashboard formats them
  // with the user's date pattern (DOCB-12248) — these are absent from the
  // static FIELD_CATALOG that `isDateValueField` checks first.
  _CUSTOM_DATE_FIELD_NAMES = new Set(
    metas
      .filter((m) => m.valueType === 'date')
      .map((m) => m.name.toLowerCase()),
  );
  return metas;
}

// MUTABLE on purpose: hydrated from `GET /document/search/fields` at app
// startup so the frontend stays in lockstep with the backend's routing
// catalog. The hardcoded entries below are the SHIPPED FALLBACK — used
// before the API responds, on offline mode, and during unit tests. Real
// runtime rendering uses whatever the API replaces this with via
// `hydrateFieldCatalog()`. See `searchFieldsCatalogService.ts`.
export const FIELD_CATALOG: FieldMeta[] = [
  // --- Standard columns (blue) ----------------------------------------
  // These are actual columns on the `documents` Postgres table — verified
  // with `information_schema.columns`. The backend filters them with plain
  // SQL; no OpenSearch round-trip needed. Category MUST stay in sync with
  // the backend's STANDARD_EQUALITY_FIELDS / STANDARD_RANGE_FIELDS in
  // routers/document_process/constants.py.
  // labelKey reuses the existing field-name i18n key (the same one
  // rendered in table column headers elsewhere in the app) — keeps chip
  // labels, column headers, and saved-filter badges consistent so the
  // user never sees two different phrasings for the same field.
  {
    name: 'id',
    category: 'standard',
    ops: ['=', '!='],
    valueType: 'uuid',
    displayName: 'Document ID',
    description: 'Document UUID (fast path)',
    descriptionKey: 'search_field_desc_id',
    labelKey: 'id',
    example: 'id=069e22da-…',
  },
  {
    name: 'filename',
    category: 'standard',
    ops: ['=', ':', '!='],
    valueType: 'string',
    displayName: 'File Name',
    description: 'File name substring',
    descriptionKey: 'search_field_desc_filename',
    labelKey: 'filename',
    example: 'filename=sps_commerce',
  },
  {
    name: 'status',
    category: 'standard',
    ops: ['=', '!='],
    valueType: 'enum',
    displayName: 'Status',
    description: 'Document status (use tabs)',
    descriptionKey: 'search_field_desc_status',
    labelKey: 'status',
    example: 'status=finished',
  },
  {
    name: 'doc_type',
    category: 'standard',
    ops: ['=', '!='],
    valueType: 'enum',
    displayName: 'Document Type',
    description: 'Document type (use tabs)',
    descriptionKey: 'search_field_desc_doc_type',
    labelKey: 'doc_type',
    example: 'doc_type=Invoice',
  },
  {
    name: 'invoice_sub_type',
    category: 'standard',
    ops: ['=', '!='],
    valueType: 'enum',
    displayName: 'Invoice Sub Type',
    description: 'Invoice sub-type (Cost Invoice / Purchase Invoice)',
    descriptionKey: 'search_field_desc_invoice_sub_type',
    labelKey: 'invoice_sub_type',
    example: 'invoice_sub_type="Cost Invoice"',
  },
  {
    // PO match outcome. Categorised 'fulltext' (orange) so it only shows for
    // fulltext-enabled orgs — this is a fulltext-only quick filter. Backend
    // still routes it through the Postgres direct column (peeled back via
    // _SQL_ONLY_RAW_QUERY_FIELDS) and expands the synthetic
    // `under_matched` / `over_matched` buckets to an IN(...) there. The three
    // QuickSearch smart filters below write the common buckets in one click.
    name: 'po_match_status',
    category: 'fulltext',
    ops: ['=', '!='],
    valueType: 'enum',
    displayName: 'PO Match Status',
    description: 'PO match outcome (full / partial / under match)',
    descriptionKey: 'search_field_desc_po_match_status',
    labelKey: 'po_match_status',
    example: 'po_match_status=full_matched',
  },
  {
    name: 'is_edoc',
    category: 'standard',
    ops: ['=', '!='],
    valueType: 'enum',
    displayName: 'Electronic Document',
    description: 'Electronic doc (XML / ZUGFeRD / Factur-X / EDI)',
    descriptionKey: 'search_field_desc_is_edoc',
    labelKey: 'search_field_is_edoc',
    example: 'is_edoc=true',
  },
  {
    name: 'pages',
    category: 'standard',
    ops: ['=', '!=', '>', '<', '>=', '<='],
    valueType: 'numeric',
    displayName: 'Pages',
    description: 'Page count',
    descriptionKey: 'search_field_desc_pages',
    labelKey: 'pages',
    example: 'pages > 1',
  },
  {
    name: 'priority',
    category: 'standard',
    ops: ['=', '!='],
    valueType: 'numeric',
    displayName: 'Priority',
    description: 'Priority level',
    descriptionKey: 'search_field_desc_priority',
    labelKey: 'priority',
    example: 'priority=1',
  },
  // Bug-25 (2026-04-23): polymorphic operator semantics.
  //   `=N`     → exact match on user_id integer (auto-routed to
  //              d.assigned_to in the PG path; OS path matches the
  //              int-typed term clause)
  //   `:Name`  → ILIKE substring on the user's full name (joined via
  //              users_cache)
  // The autocomplete dropdown picks a user and writes the integer
  // user_id into the chip (value: userObj.id), so `=N` is the
  // canonical "I picked this user" path. `:Daniel` stays the
  // free-text path for typing partial names.
  {
    name: 'assigned_to',
    category: 'standard',
    ops: ['=', ':', '!='],
    valueType: 'string',
    displayName: 'Assigned To',
    description: 'Assigned user (= for exact ID, : for name substring)',
    descriptionKey: 'search_field_desc_assigned_to',
    labelKey: 'assigned_to',
    example: 'assigned_to=204 or assigned_to:Daniel',
    supportsPresence: true,
  },
  {
    name: 'created_by',
    category: 'standard',
    ops: ['=', ':', '!='],
    valueType: 'string',
    displayName: 'Uploaded By',
    description: 'Uploader (= for exact ID, : for name substring)',
    descriptionKey: 'search_field_desc_created_by',
    labelKey: 'created_by',
    example: 'created_by=204 or created_by:Daniel',
    supportsPresence: true,
  },
  {
    name: 'last_saved_by',
    category: 'standard',
    ops: ['=', ':', '!='],
    valueType: 'string',
    displayName: 'Last Edited By',
    description: 'Last editor (= for exact ID, : for name substring)',
    descriptionKey: 'search_field_desc_last_saved_by',
    labelKey: 'last_saved_by',
    example: 'last_saved_by=204 or last_saved_by:Daniel',
    supportsPresence: true,
  },
  // `sub_org_name` isn't a pre-translated key on its own; the existing
  // `sub_org` key covers the same concept in the UI.
  {
    name: 'sub_org_name',
    category: 'standard',
    ops: ['=', '!='],
    valueType: 'string',
    displayName: 'Sub-Organisation',
    description: 'Sub-organisation',
    descriptionKey: 'search_field_desc_sub_org_name',
    labelKey: 'sub_org',
    example: 'sub_org_name=Berlin',
    supportsPresence: true,
  },
  // `imported_on` / `created_on` and `updated` / `last_modified_on` are the
  // same underlying column pairs. We expose BOTH names — the user-friendly
  // alias and the backend-canonical identifier — so autocomplete suggests
  // whichever the user started typing. The canonical name only gets swapped
  // in at the backend-serialisation boundary (see `canonicaliseForBackend`),
  // never in the chip representation.
  {
    name: 'imported_on',
    category: 'standard',
    ops: ['between', '>=', '<='],
    valueType: 'date',
    displayName: 'Import Date',
    description: 'Import date',
    descriptionKey: 'search_field_desc_imported_on',
    labelKey: 'imported_on',
    example: 'imported_on >= 2026-04-01',
  },
  {
    name: 'created_on',
    category: 'standard',
    ops: ['between', '>=', '<='],
    valueType: 'date',
    displayName: 'Created',
    description: 'Creation date',
    descriptionKey: 'search_field_desc_created_on',
    labelKey: 'created_on',
    example: 'created_on >= 2026-01-01',
  },
  {
    name: 'updated',
    category: 'standard',
    ops: ['between', '>=', '<='],
    valueType: 'date',
    displayName: 'Last Modified',
    description: 'Last modified',
    descriptionKey: 'search_field_desc_updated',
    labelKey: 'updated',
    example: 'updated >= 2026-04-01',
  },
  {
    name: 'last_modified_on',
    category: 'standard',
    ops: ['between', '>=', '<='],
    valueType: 'date',
    displayName: 'Last Modified',
    description: 'Last modified date',
    descriptionKey: 'search_field_desc_last_modified_on',
    labelKey: 'last_modified_on',
    example: 'last_modified_on >= 2026-04-01',
  },
  // DOCB-12248: assigned_on is a real DateTime column (DASHBOARD_STANDARD_COLUMNS)
  // whose dashboard value comes straight from the DB, so it's a reliable
  // searchable date field. The backend catalog is the source of truth
  // (search_fields_catalog.py); this seed entry is the bootstrap/offline
  // fallback that `hydrateFieldCatalog` overrides at runtime.
  //
  // locked_on is intentionally NOT seeded here: its dashboard value is
  // Redis-sourced (update_documents_lock_status), so it must not be advertised
  // as a DB-filterable field. Components that render it (e.g.
  // DuplicateDocumentList) format it as a date via their own local logic.
  {
    name: 'assigned_on',
    category: 'standard',
    ops: ['between', '>=', '<='],
    valueType: 'date',
    displayName: 'Assigned On',
    description: 'Assignment date',
    descriptionKey: 'search_field_desc_assigned_on',
    labelKey: 'assigned_on',
    example: 'assigned_on >= 2026-04-01',
  },

  // --- Extracted / CALC fields ----------------------------------------
  // ALL extracted fields START as 'fulltext'. The promotion to
  // 'extended' (teal — SQL-fast, no fulltext license needed) happens
  // dynamically at runtime via two paths:
  //   1. Backend hydration: GET /document/search/fields ships the
  //      catalog with `category: "extended"` for any field projected
  //      onto a SYNC_COLUMNS column. `hydrateFieldCatalog` replaces
  //      this hardcoded list with the canonical backend version.
  //   2. Custom doc-type fields: see `customFieldsToFieldMetas` —
  //      reads `extendedFieldNames` from the same response and tags
  //      org-specific custom fields the same way.
  //
  // Single source of truth: `constants/clickhouse.py:SYNC_COLUMNS` on
  // the backend. Adding a new projected field there auto-promotes it
  // here without any frontend change.
  {
    name: 'invoice_id',
    category: 'fulltext',
    ops: ['=', ':', '!='],
    valueType: 'string',
    description: 'Invoice number / ID',
    descriptionKey: 'search_field_desc_invoice_id',
    labelKey: 'invoice_id',
    example: 'invoice_id=INV-2026-03-00284',
    supportsPresence: true,
  },
  {
    name: 'purchase_order',
    category: 'fulltext',
    ops: ['=', ':', '!='],
    valueType: 'string',
    description: 'Purchase order number',
    descriptionKey: 'search_field_desc_purchase_order',
    labelKey: 'purchase_order',
    example: 'purchase_order:"PO 42"',
    supportsPresence: true,
  },
  {
    name: 'invoice_date',
    category: 'fulltext',
    ops: ['between', '>=', '<='],
    valueType: 'date',
    description: 'Invoice date',
    descriptionKey: 'search_field_desc_invoice_date',
    labelKey: 'invoice_date',
    example: 'invoice_date >= 2026-01-01',
    supportsPresence: true,
  },
  {
    name: 'supplier_name',
    category: 'fulltext',
    ops: ['=', ':', '!='],
    valueType: 'string',
    description: 'Supplier / vendor name',
    descriptionKey: 'search_field_desc_supplier_name',
    labelKey: 'supplier_name',
    example: 'supplier_name:"FUJI"',
    supportsPresence: true,
  },
  {
    name: 'ap_assignment_code',
    category: 'fulltext',
    ops: ['=', '!='],
    valueType: 'string',
    description: 'AP vendor assignment code (use !="" for "has a value")',
    descriptionKey: 'search_field_desc_ap_assignment_code',
    labelKey: 'ap_assignment_code',
    example: 'ap_assignment_code != ""',
    supportsPresence: true,
  },
  {
    name: 'total_amount',
    category: 'fulltext',
    ops: ['=', '!=', '>', '<', '>=', '<='],
    valueType: 'numeric',
    description: 'Invoice total',
    descriptionKey: 'search_field_desc_total_amount',
    labelKey: 'total_amount',
    example: 'total_amount > 1500',
    supportsPresence: true,
  },
  {
    name: 'net_amount',
    category: 'fulltext',
    ops: ['=', '!=', '>', '<', '>=', '<='],
    valueType: 'numeric',
    description: 'Invoice net',
    descriptionKey: 'search_field_desc_net_amount',
    labelKey: 'net_amount',
    example: 'net_amount > 1000',
    supportsPresence: true,
  },
  {
    name: 'tax_amount',
    category: 'fulltext',
    ops: ['=', '!=', '>', '<', '>=', '<='],
    valueType: 'numeric',
    description: 'Invoice tax',
    descriptionKey: 'search_field_desc_tax_amount',
    labelKey: 'tax_amount',
    example: 'tax_amount > 100',
    supportsPresence: true,
  },
  {
    name: 'invoice_due_date',
    category: 'fulltext',
    ops: ['between', '>=', '<='],
    valueType: 'date',
    description: 'Invoice due date',
    descriptionKey: 'search_field_desc_invoice_due_date',
    labelKey: 'invoice_due_date',
    example: 'invoice_due_date >= 2026-04-01',
    supportsPresence: true,
  },
  {
    name: 'discount_due_date',
    category: 'fulltext',
    ops: ['between', '>=', '<='],
    valueType: 'date',
    description: 'Discount due date',
    descriptionKey: 'search_field_desc_discount_due_date',
    labelKey: 'discount_due_date',
    example: 'discount_due_date >= 2026-03-01',
    supportsPresence: true,
  },
  {
    name: 'payment_terms',
    category: 'fulltext',
    ops: ['=', '!='],
    valueType: 'enum',
    description: 'Payment terms code',
    descriptionKey: 'search_field_desc_payment_terms',
    labelKey: 'payment_terms',
    example: 'payment_terms=NET30',
    supportsPresence: true,
  },
  {
    name: 'discount_term',
    category: 'fulltext',
    ops: ['=', '!='],
    valueType: 'enum',
    description: 'Discount term',
    descriptionKey: 'search_field_desc_discount_term',
    labelKey: 'discount_term',
    example: 'discount_term=2/10',
    supportsPresence: true,
  },
  {
    name: 'vendor_id',
    category: 'fulltext',
    ops: ['=', '!='],
    valueType: 'numeric',
    description: 'Vendor identifier',
    descriptionKey: 'search_field_desc_vendor_id',
    labelKey: 'vendor',
    example: 'vendor_id=12345',
    supportsPresence: true,
  },
  // `vendor_name` is the procurement-side alias for `supplier_name`.
  // Canonicalised to `supplier_name` at the HTTP boundary (FIELD_ALIASES
  // in canonicaliseForBackend), where the canonical column IS in
  // SYNC_COLUMNS. The chip itself is tagged 'fulltext' to keep the
  // catalog-symmetry assertion strict — the alias is wire-only.
  {
    name: 'vendor_name',
    category: 'fulltext',
    ops: ['=', ':', '!='],
    valueType: 'string',
    description: 'Vendor / supplier name',
    descriptionKey: 'search_field_desc_supplier_name',
    labelKey: 'vendor_name',
    example: 'vendor_name:"FUJI"',
    supportsPresence: true,
  },
  {
    name: 'currency',
    category: 'fulltext',
    ops: ['=', '!='],
    valueType: 'enum',
    description: 'ISO currency code',
    descriptionKey: 'search_field_desc_currency',
    labelKey: 'currency',
    example: 'currency=EUR',
  },

  // --- Vector (purple) --------------------------------------------
  // `vector:` is pure kNN — the backend embeds the query string and
  // returns documents whose content embeddings are nearest in vector
  // space. No filter extraction, no parsing — just "what's similar to
  // this?". Requires an OPENSEARCH_VECTOR_* index for the org.
  {
    name: 'vector:',
    category: 'vector',
    ops: [':', '='],
    valueType: 'string',
    description:
      'Semantic similarity search — finds documents whose content matches the meaning of your query',
    descriptionKey: 'search_field_desc_vector',
    labelKey: 'search_field_label_vector',
    example: 'vector: frozen food from Ruiz',
  },
  // --- AI (separate routing path from vector) ----------------------
  // `ai:` is NOT a vector alias. It routes through fulltextsearch's
  // ai_query_parser (an Ollama-hosted LLM), which reads the natural-
  // language phrase and rewrites it into a structured query —
  // extracting filters like supplier, date range, amount thresholds.
  // Lives in its own autocomplete category so the dropdown doesn't
  // imply equivalence with `vector:`. Requires fulltext on the org;
  // the vector index is irrelevant here.
  {
    name: 'ai:',
    category: 'ai',
    ops: [':', '='],
    valueType: 'string',
    description:
      'Natural-language query — the AI reads your phrase and extracts filters (supplier, dates, amounts) automatically',
    descriptionKey: 'search_field_desc_ai',
    labelKey: 'search_field_label_ai',
    example: 'ai: invoices from Ruiz over 1000 last quarter',
  },
  {
    name: 'ocr:',
    category: 'ocr',
    ops: [':', '='],
    valueType: 'string',
    description: 'OCR and table text search prefix',
    descriptionKey: 'search_field_desc_ocr',
    labelKey: 'search_field_label_ocr',
    example: 'ocr: delivery address',
  },
] as const;

// ── Smart Filters (business-language quick predicates) ───────────────
//
// Curated one-click filters with a non-technical label. The picker
// surfaces them as their own group and emits a complete predicate
// expression (possibly multi-clause with AND/OR) when the user selects
// one — the Editor commits the bundle as a single chip per clause via
// the shared chip-commit pipeline.
//
// `buildPredicate({ userId })` returns the raw predicate string.
// `userId` is supplied by the host so `assigned_to=<id>` resolves to
// the actual signed-in user. Filters that don't reference the user
// just ignore the parameter.
//
// `matchKeywords` are the substrings the autocomplete will accept the
// user typing — keyed by locale so a German user types "überfällig"
// and an Italian one types "scaduto" without ever seeing the other's
// shortcut. The active locale is resolved at filter-time via
// `i18n.getLanguage()`; unknown locales fall back to `en` so the
// product still works for languages where keywords aren't pflegt yet.
//
// ASCII-fold variants (`ueberfaellig` for `überfällig`, `aujourdhui`
// for `aujourd'hui`) are included alongside the diacritic form for
// keyboards without easy access to the special chars — the picker
// does substring matching, so both find the same row.
export type SmartFilterLocale = 'en' | 'de' | 'es' | 'fr' | 'it';
export interface SmartFilterCtx {
  /** The signed-in user's numeric id; used by personal filters. */
  userId?: number | string | null;
}
export interface SmartFilter {
  name: string;
  labelKey: string;
  descriptionKey: string;
  icon: string;
  buildPredicate: (ctx: SmartFilterCtx) => string;
  matchKeywords: Readonly<
    Partial<Record<SmartFilterLocale, readonly string[]>>
  >;
}

export const SMART_FILTERS: readonly SmartFilter[] = [
  {
    name: 'overdue',
    labelKey: 'search_smart_filter_overdue',
    descriptionKey: 'search_smart_filter_overdue_desc',
    icon: 'warning',
    buildPredicate: () => 'invoice_due_date<today()',
    matchKeywords: {
      en: ['overdue', 'late', 'past due'],
      de: ['überfällig', 'ueberfaellig', 'mahnung', 'verspätet', 'verspaetet'],
      es: ['vencido', 'atrasado', 'mora', 'caducado'],
      fr: ['échu', 'echu', 'retard', 'expiré', 'expire'],
      it: ['scaduto', 'in ritardo', 'ritardo'],
    },
  },
  {
    name: 'due_soon',
    labelKey: 'search_smart_filter_due_soon',
    descriptionKey: 'search_smart_filter_due_soon_desc',
    icon: 'schedule',
    // Single clause for Phase 1 — covers everything due within 7 days,
    // including overdue. Pair with the Overdue filter to slice out the
    // strictly-future portion if the user wants. A multi-clause version
    // (`>= today() AND <= today()+7`) is on the Phase-2 backlog once
    // the chip-editor commits clause-bundles atomically.
    buildPredicate: () => 'invoice_due_date<=today()+7',
    matchKeywords: {
      en: ['due soon', 'next 7', 'this week'],
      de: [
        'bald fällig',
        'bald faellig',
        'nächste woche',
        'naechste woche',
        'fällig',
      ],
      es: ['pronto', 'vencer', 'próximo', 'proximo', 'próxima', 'proxima'],
      fr: [
        'bientôt',
        'bientot',
        'échéance',
        'echeance',
        'cette semaine',
        'prochain',
      ],
      it: [
        'in scadenza',
        'scadenza',
        'prossimo',
        'prossima',
        'questa settimana',
      ],
    },
  },
  {
    name: 'assigned_to_me',
    labelKey: 'search_smart_filter_assigned_to_me',
    descriptionKey: 'search_smart_filter_assigned_to_me_desc',
    icon: 'person',
    // When userId is missing (rare boot race) we still emit the literal
    // `me` token; the parser will surface it as "no match" rather than
    // silently filter to a wrong user.
    buildPredicate: ({ userId }) =>
      `assigned_to=${userId !== null && userId !== undefined ? userId : 'me'}`,
    matchKeywords: {
      en: ['my', 'me', 'mine', 'assigned to me'],
      de: ['mir', 'meine', 'mein', 'zugewiesen'],
      es: ['mí', 'mio', 'mío', 'asignado', 'asignada', 'a mí'],
      fr: ['moi', 'mien', 'mienne', 'assigné', 'assignee', 'à moi'],
      it: ['mio', 'mia', 'assegnato', 'assegnata', 'a me'],
    },
  },
  {
    name: 'today_inbox',
    labelKey: 'search_smart_filter_today',
    descriptionKey: 'search_smart_filter_today_desc',
    icon: 'today',
    buildPredicate: () => 'imported_on>=today()',
    matchKeywords: {
      en: ['today', 'inbox', 'incoming'],
      de: ['heute', 'eingang', 'posteingang'],
      es: ['hoy', 'bandeja', 'entrada'],
      fr: ["aujourd'hui", 'aujourdhui', 'reception', 'réception', 'boîte'],
      it: ['oggi', 'in arrivo', 'arrivo'],
    },
  },
  {
    name: 'pending_validation',
    labelKey: 'search_smart_filter_pending_validation',
    descriptionKey: 'search_smart_filter_pending_validation_desc',
    icon: 'pending_actions',
    buildPredicate: () => 'status=ready_for_validation',
    matchKeywords: {
      en: ['pending', 'validation', 'review'],
      de: ['wartet', 'prüfen', 'pruefen', 'freigabe', 'validierung'],
      es: [
        'pendiente',
        'validación',
        'validacion',
        'revisar',
        'revisión',
        'revision',
      ],
      fr: [
        'en attente',
        'attente',
        'à valider',
        'a valider',
        'révision',
        'revision',
      ],
      it: [
        'in attesa',
        'attesa',
        'da validare',
        'validare',
        'da rivedere',
        'revisione',
      ],
    },
  },
  {
    // Single boolean lane covering ALL electronic-document formats:
    // pure XML (XRechnung, CII, UBL, EHF, Finvoice, …), ZUGFeRD- and
    // Factur-X-embedded PDFs, plain EDI/EDIFACT. The pipeline flips
    // documents.is_edoc=true after a matching classification rule fires;
    // see abc_electronic_document_processor.classify_document_type. Replaces
    // OR-chains over has_zugferd / has_peppol / per-format doc_type.
    name: 'edocs',
    labelKey: 'search_smart_filter_edocs',
    descriptionKey: 'search_smart_filter_edocs_desc',
    icon: 'receipt_long',
    buildPredicate: () => 'is_edoc=true',
    matchKeywords: {
      en: ['edocs', 'edoc', 'e-doc', 'electronic documents', 'electronic'],
      de: [
        'edocs',
        'edoc',
        'e-dokumente',
        'elektronische dokumente',
        'elektronisch',
      ],
      es: ['edocs', 'documentos electrónicos', 'electronicos', 'electrónico'],
      fr: ['edocs', 'documents électroniques', 'electroniques', 'électronique'],
      it: ['edocs', 'documenti elettronici', 'elettronico'],
    },
  },
  // PO match status buckets — one-click filters over documents.po_match_status.
  // `full`/`partial` map 1:1 to a status; `under` is a SYNTHETIC bucket token
  // the backend (PO_MATCH_STATUS_GROUPS) expands to an IN(...) over
  // quantity_under_matched + unit_price_under_matched — kept a single clean
  // token so neither query parser needs comma/OR handling.
  {
    name: 'po_full_match',
    labelKey: 'search_smart_filter_po_full_match',
    descriptionKey: 'search_smart_filter_po_full_match_desc',
    icon: 'check_circle',
    buildPredicate: () => 'po_match_status=full_matched',
    matchKeywords: {
      en: ['full match', 'fully matched', 'po match', 'matched'],
      de: ['vollständig', 'vollstaendig', 'voll abgeglichen', 'po treffer'],
      es: ['coincidencia total', 'totalmente', 'emparejado'],
      fr: ['correspondance totale', 'totalement', 'rapproché', 'rapproche'],
      it: ['corrispondenza completa', 'completo', 'abbinato'],
    },
  },
  {
    name: 'po_partial_match',
    labelKey: 'search_smart_filter_po_partial_match',
    descriptionKey: 'search_smart_filter_po_partial_match_desc',
    icon: 'rule',
    buildPredicate: () => 'po_match_status=partial_matched',
    matchKeywords: {
      en: ['partial match', 'partially matched', 'half match', 'partial'],
      de: ['teilweise', 'teil abgeglichen', 'teiltreffer', 'halb'],
      es: ['coincidencia parcial', 'parcial', 'parcialmente'],
      fr: ['correspondance partielle', 'partielle', 'partiellement'],
      it: ['corrispondenza parziale', 'parziale', 'parzialmente'],
    },
  },
  {
    name: 'po_under_match',
    labelKey: 'search_smart_filter_po_under_match',
    descriptionKey: 'search_smart_filter_po_under_match_desc',
    icon: 'trending_down',
    buildPredicate: () => 'po_match_status=under_matched',
    matchKeywords: {
      en: ['under match', 'under matched', 'under delivery', 'short', 'shortfall'],
      de: ['unterlieferung', 'untertreffer', 'zu wenig', 'unter abgeglichen'],
      es: ['coincidencia inferior', 'subentrega', 'por debajo'],
      fr: ['sous-correspondance', 'sous-livraison', 'insuffisant'],
      it: ['corrispondenza inferiore', 'sotto consegna', 'insufficiente'],
    },
  },
];

// Canonical UUID v1–v5 pattern. Used to short-circuit the autocomplete
// when the user pastes a document id, and to auto-promote a bare UUID
// to an `id=<uuid>` predicate in parseSearchQuery so the Postgres fast
// path is hit without the user typing `id=` manually.
// Tolgee i18n keys for the short hint shown next to each operator in the
// autocomplete's operator-mode list. Centralised here so the composable
// (useQuickSearchAutocomplete) and the renderer (FieldAutocomplete.vue)
// read from one source and the translations live with the rest of the
// search UI (search_field_desc_*, search_connector_and_hint, …).
export const OPERATOR_HINTS: Record<string, string> = {
  '=': 'search_op_hint_eq',
  ':': 'search_op_hint_contains',
  '>': 'search_op_hint_gt',
  '<': 'search_op_hint_lt',
  '>=': 'search_op_hint_gte',
  '<=': 'search_op_hint_lte',
  between: 'search_op_hint_between',
  '!=': 'search_op_hint_neq',
};

export const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function getFieldMeta(name: string): FieldMeta | undefined {
  const canonical = FIELD_ALIASES[name.toLowerCase()] || name.toLowerCase();
  return FIELD_CATALOG.find((f) => f.name === canonical);
}

// Capability-independent date-column fallback. `GET /document/search/fields`
// is capability-filtered (filter_catalog_by_capabilities): non-fulltext orgs
// receive a catalog WITHOUT fulltext date fields (invoice_date,
// invoice_due_date, discount_due_date), and `hydrateFieldCatalog` REPLACES
// FIELD_CATALOG in-place with that filtered list. Those are still date
// COLUMNS the dashboard must format — date FORMATTING must not depend on the
// org's SEARCH capability. Snapshot the static seed's date fields at module
// load (before any hydration mutates FIELD_CATALOG) and use them as a final
// fallback. Derived from the single seed definition, so no dual maintenance.
const _STATIC_DATE_FIELD_NAMES: ReadonlySet<string> = new Set(
  FIELD_CATALOG.filter((f) => f.valueType === 'date').map((f) => f.name),
);

// DOCB-12248: data-driven "is this column a date?" predicate. The dashboard
// uses it to decide whether a cell goes through the locale-aware date
// formatter. Resolution order: hydrated FIELD_CATALOG (incl. aliases, the
// API source of truth) → custom doc-type DATE fields (`_CUSTOM_DATE_FIELD_NAMES`,
// hydrated via `customFieldsToFieldMetas`) → capability-independent static
// seed (`_STATIC_DATE_FIELD_NAMES`) so capability-filtered date columns still
// format.
export function isDateValueField(name: string): boolean {
  if (!name) {
    return false;
  }
  const meta = getFieldMeta(name);
  if (meta) {
    return meta.valueType === 'date';
  }
  const lower = name.toLowerCase();
  if (_CUSTOM_DATE_FIELD_NAMES.has(lower)) {
    return true;
  }
  const canonical = FIELD_ALIASES[lower] || lower;
  return _STATIC_DATE_FIELD_NAMES.has(canonical);
}

// Extracted-only fields — no Postgres column exists, so even `=` needs Fulltext.
const EXTRACTED_ONLY_FIELDS: ReadonlySet<string> = new Set([
  'ap_assignment_code',
  'invoice_due_date',
  'due_date', // Bug-23 alias for invoice_due_date — both need fulltext
  'discount_due_date',
  'total_amount',
  'net_amount',
  'tax_amount',
  'discount_term',
  'payment_terms',
]);

// Tokenise respecting quoted strings. "foo bar" stays a single token.
// Parentheses `(` and `)` are lexical grouping markers — emitted as
// standalone single-char tokens outside quotes so the caller can
// distinguish `(status=error)` from `status=error` without re-scanning
// the raw input. Inside quotes they're literal characters of the value.
export function tokenise(input: string): string[] {
  const tokens: string[] = [];
  let buf = '';
  let inQuote = false;
  let quoteChar = '';
  // Depth of function-call nesting we're currently inside. Bumped on
  // `(` that immediately follows an identifier char; decremented on the
  // matching `)`. While depth > 0, EVERY character (including spaces
  // and parens) stays in `buf` so `today()-1`, `today(-1)`, and
  // `coalesce(a, b)` all survive as single tokens.
  let fnDepth = 0;
  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (fnDepth > 0) {
      buf += ch;
      if (ch === '(') fnDepth++;
      else if (ch === ')') fnDepth--;
      continue;
    }
    if (inQuote) {
      buf += ch;
      if (ch === quoteChar) inQuote = false;
    } else if (ch === '"' || ch === "'") {
      inQuote = true;
      quoteChar = ch;
      buf += ch;
    } else if (/\s/.test(ch)) {
      if (buf) tokens.push(buf);
      buf = '';
    } else if (
      ch === '(' &&
      buf.length > 0 &&
      /[a-zA-Z0-9_]/.test(buf[buf.length - 1])
    ) {
      // Open paren directly after an identifier char — function-call
      // literal. Keep it + all subsequent chars until the matching `)`
      // inside `buf` so relative-date expansion still sees the intact
      // literal rather than three fragments.
      buf += ch;
      fnDepth = 1;
    } else if (ch === '(' || ch === ')') {
      if (buf) tokens.push(buf);
      buf = '';
      tokens.push(ch);
    } else {
      buf += ch;
    }
  }
  if (buf) tokens.push(buf);
  return tokens;
}

// Exported for callers that need to identify parenthesis tokens after
// tokenise() — e.g. the editor deserializer that creates atomic paren
// nodes alongside chip nodes.
export const PAREN_OPEN_TOKEN = '(';
export const PAREN_CLOSE_TOKEN = ')';
export function isParenToken(tok: string): boolean {
  return tok === PAREN_OPEN_TOKEN || tok === PAREN_CLOSE_TOKEN;
}

// Strip a single pair of surrounding quotes. Preserves internal quotes.
function unquote(value: string): string {
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    return value.slice(1, -1);
  }
  return value;
}

// field OP value  where OP is one of the supported operators.
// Longer operators first so >/< don't win greedily over >=, <=, !=.
// The value group deliberately rejects inputs that START with another
// operator character (`[=:<>!]`). Without that guard, a typo like
// `filename := Test` would collapse to `filename:=Test` and the regex
// would greedily match op=`:`, value=`=Test` — producing a predicate
// that nobody asked for and silently hiding real filter failures.
// Quoted values (`purchase_order:"PO 42"`) and numeric/alpha values
// are unaffected; bracketed variants fall back to the keyword path
// where they belong.
const PREDICATE_RE = /^([a-zA-Z_][a-zA-Z0-9_]*)(>=|<=|!=|=|:|>|<)([^=:<>!].*)$/;

// Relative-date literals the user can type in place of an ISO date.
// `today()` / `now()` (same thing — Dashboard is day-granular, not
// hour-granular) + optional `±N` day-offset. Kept client-side so the
// expanded date travels the HTTP/WS boundary as a plain ISO string.
const RELATIVE_DATE_RE = /^(today|now)\(\)(?:([+-])(\d+))?$/i;

/**
 * Expand a relative-date literal (`today()`, `today()-9`, `now()+7`)
 * into an ISO `YYYY-MM-DD` string. Returns the input unchanged if it
 * isn't a recognised literal — the caller can still pass absolute
 * dates like `2026-04-19` or `17.04.2026` through.
 */
export function expandRelativeDate(value: string): string {
  if (!value) return value;
  const m = RELATIVE_DATE_RE.exec(value.trim());
  if (!m) return value;
  const sign = m[2];
  const n = m[3] ? parseInt(m[3], 10) : 0;
  const delta = sign === '-' ? -n : n;
  const d = new Date();
  d.setDate(d.getDate() + delta);
  // Format as YYYY-MM-DD in local time (not toISOString — that's UTC
  // and would flip the day for users east of London after ~midnight).
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Opt-in semantic search prefix. User types `vector: ...` (or the
// shorter `ai: ...` alias) to switch the rest of the query to
// vector-similarity mode instead of keyword. Both `:` and `=` are
// accepted because users naturally type either — the field-equality
// shape `ai=foo` would otherwise route to a non-existent `ai` field
// and the backend would 422 the request.
//
// Any of these all parse identically:
//   vector: Bürobedarf
//   vector:Bürobedarf
//   vector=Bürobedarf
//   ai: Bürobedarf
//   ai:Bürobedarf
//   ai=Bürobedarf
// Bug-2026-04-29 (user spec: "ai soll nur ai machen und vector nur vector"):
// vector: and ai: are routed to two different services in the backend
// (kNN vs Ollama LLM) and must NOT share the `is_vector` wire flag.
// Previously a single `vector|ai` regex collapsed both into isVectorMode,
// which made the frontend send is_fulltext=true&is_vector=true for an
// `ai:` query — the backend's vector branch then either preempted the
// AI dispatch OR caused the request to hang for 60s+ because nginx
// timed out before the AI service responded. Splitting the regex makes
// the two prefixes routable independently.
// 2026-05-18: accept both `vector:` and `semantic:` as the explicit
// trigger for vector / semantic search. Per the user-stated rule
// ("vector ist nur wenn man nach vector oder semantic sucht sonst nie")
// these are the ONLY two prefixes that enable kNN — every other input
// must remain plain fulltext.
export const VECTOR_PREFIX_RE = /^\s*(?:vector|semantic)\s*[:=]\s*/i;
export const AI_PREFIX_RE = /^\s*ai\s*[:=]\s*/i;
export const OCR_PREFIX_RE = /^\s*ocr\s*[:=]\s*/i;
// Combined matcher (for "is this any semantic-query prefix?" checks
// that don't care which one it is — chip-editor classification, etc.).
export const SEMANTIC_PREFIX_RE = /^\s*(?:vector|ai|ocr)\s*[:=]\s*/i;
// Captures the prefix keyword so the chip editor can tell `vector:` and
// `ai:` apart when committing a semantic-query chip.
export const VECTOR_PREFIX_CAPTURE_RE = /^\s*(vector|ai|ocr)\s*[:=]\s*(.*)$/i;

// `field between X and Y` — sugar for `field>=X AND field<=Y`. Works
// for any field that supports range operators (dates, amounts, pages,
// …). The value tokens are either a bare word (no whitespace) or a
// quoted string — captured intact so relative-date literals like
// `today()` or `today()-7` survive the expansion.
//
// Example rewrites applied before the main parser runs:
//   invoice_due_date between 2026-01-01 and 2026-04-30
//     → invoice_due_date>=2026-01-01 AND invoice_due_date<=2026-04-30
//   total_amount between 1000 and 5000
//     → total_amount>=1000 AND total_amount<=5000
//   imported_on between today()-30 and today()
//     → imported_on>=today()-30 AND imported_on<=today()
// Bug-2026-05-25 #7: accept BOTH `between A, B` (comma form, what the
// frontend chip UI generates) AND `between A and B` (SQL-natural form,
// what users type in Raw mode and what the doc2-api `_expand_between`
// already accepts).  Previously only the comma form matched, so a Raw
// query like `imported_on between 2026-05-23 and 2026-05-25` left the
// `between` token unexpanded — the outer parser flagged a syntax error
// and the dashboard fell back to "show all".  Two separators, one
// expansion target.
// Bug-2026-05-25 #17/#21/#31/#37/#38/#39/#41 (Codex v3-v7): the
// field-name capture must be in PREDICATE position, not VALUE
// position.  After six rounds of positive-boundary attempts (each
// with corner cases), the discriminator that handles them all is a
// single NEGATIVE lookbehind: a value position is one preceded by a
// predicate OPERATOR (`:=<>!`) optionally followed by whitespace
// and/or open-parens.  Everything else — start of string, after a
// logical connector, after an open paren at a boundary, OR after
// free-text keywords — is a predicate position and expands.
//
// `(?<![:=<>!][\s(]*)` reads: "not immediately preceded by an
// operator char followed by any run of whitespace/open-parens".
// This is a VARIABLE-LENGTH lookbehind (supported by V8/Node).
//
//   filename: total_amount between …   → `: ` before → BLOCKED (#21)
//   filename:(total_amount between …)  → `:(` before → BLOCKED (#37)
//   filename: (total_amount between …) → `: (` before → BLOCKED
//   ( total_amount between …)          → `( ` (no op) → expands (#31)
//   acme total_amount between …        → `acme ` (no op) → expands (#39)
//   status=x AND total_amount between…→ `AND ` (no op) → expands
//
// The output is wrapped in parens (see expandBetween) so a leading
// `NOT` or surrounding `AND`/`OR` keeps correct boolean precedence
// (Codex v7 #40/#41).
const BETWEEN_RE =
  /(?<![:=<>!][\s(]*)\b([a-zA-Z_][a-zA-Z0-9_]*)\s+between\s+("[^"]*"|'[^']*'|[^\s,]+)\s*(?:,|\s+and\s+)\s*("[^"]*"|'[^']*'|\S+)/gi;

// Helper: protect quoted spans and semantic-prefix payloads from the
// BETWEEN/NOT BETWEEN expansion.  See Bug-2026-05-25 #9, #12 and #16.
// Smart-quote variants are normalised first so curly quotes from
// macOS/Word are protected as well.
const QUOTED_SPAN_RE_BETWEEN = /"[^"]*"|'[^']*'/g;

function withQuotesAndPrefixesProtected(
  input: string,
  applyExpansion: (masked: string) => string,
): string {
  // Bug-2026-05-25 #16 (Codex v3 [high]): `canonicaliseForBackend`
  // calls expandBetween BEFORE the main parser's smart-quote
  // normaliser runs, so `filename:"invoice_date between …"` with
  // curly quotes from macOS auto-correct was invisible to the
  // straight-quote mask below.  Normalise smart quotes here first
  // so every entry point reaches the mask with `"`/`'` only.
  input = normaliseSmartQuotes(input);
  // Bug-2026-05-25 #12 (Codex v2): semantic-prefix queries carry
  // free-text / embedding payloads, NOT structured predicates.  Skip
  // BETWEEN expansion entirely so `ai: total_amount between 1000
  // and 5000` (with a known field name) doesn't get rewritten.
  if (VECTOR_PREFIX_RE.test(input) || SEMANTIC_PREFIX_RE.test(input)) {
    return input;
  }
  // Mask quoted spans before applying the regex so
  // `filename:"invoice_date between 2024 and 2025"` doesn't trigger
  // expansion inside the quoted literal — even when the word inside
  // happens to match a known field name.
  const masks: string[] = [];
  let masked = input.replace(QUOTED_SPAN_RE_BETWEEN, (m) => {
    const idx = masks.length;
    masks.push(m);
    return `\x00BTWMASK${idx}\x00`;
  });
  masked = applyExpansion(masked);
  return masked.replace(/\x00BTWMASK(\d+)\x00/g, (_m, idx) => {
    return masks[parseInt(idx, 10)];
  });
}

/**
 * Rewrite `field between X and Y` to `field>=X AND field<=Y` before
 * the main parser runs. Non-matching input passes through unchanged.
 *
 * Exported so the unit tests can assert the expansion shape without
 * re-running the whole parser.
 *
 * Bug-2026-05-25 #9 (Codex adversarial review v1): the BETWEEN_RE
 * regex matches any `<word> between <token> (,|and) <token>`
 * substring — not just real field predicates.  Field-validator
 * guard (`isSupportedSearchField`) added to block unknown field
 * names.
 *
 * Bug-2026-05-25 #12 (Codex adversarial review v2): the field-
 * validator alone leaked through TWO cases:
 *   - `filename:"invoice_date between 2024 and 2025"` — `invoice_date`
 *     IS a supported field, so the validator passed and the quoted
 *     value was corrupted.
 *   - `ai: total_amount between 1000 and 5000` — `total_amount` IS
 *     a supported field, so the semantic-prefix payload was
 *     rewritten into structured comparison syntax.
 *
 * Hardening: protect quoted spans (mask before regex, restore
 * after) AND skip semantic-prefix queries entirely.  Both guards
 * run BEFORE the field-validator, so even known field names inside
 * those protected contexts are left intact.
 */
export function expandBetween(input: string): string {
  return withQuotesAndPrefixesProtected(input, (masked) =>
    masked.replace(BETWEEN_RE, (match, field, lo, hi) => {
      if (!isSupportedSearchField(field.toLowerCase())) {
        return match;
      }
      // Bug #40/#41 (Codex v7): wrap in parens so a leading `NOT` or
      // a surrounding `X AND … AND Y` keeps correct boolean
      // precedence — without the group, `NOT a between 1 and 2`
      // would bind NOT to only the first conjunct, and
      // `x AND a between 1 and 2 AND y` would mis-scope.
      return `(${field}>=${lo} AND ${field}<=${hi})`;
    }),
  );
}

// --- Convenience-syntax expansions ------------------------------------
//
// Every function below takes a string and returns a string. They run
// BEFORE the main parse loop, so the rest of the parser sees only the
// canonical `field<op>value` / AND / OR form. This keeps the existing
// tokeniser + predicate-builder trivial.
//
// Ordering in `parseSearchQuery` is load-bearing — see the header
// comment there for the rationale behind the sequence.

// ── Smart quotes (Phase C-15) ────────────────────────────────────────
// Macs and Word turn "foo" into “foo” on the fly. The tokeniser only
// recognises straight quotes, so we normalise up-front.
const SMART_QUOTE_MAP: Record<string, string> = {
  '\u201C': '"', // “
  '\u201D': '"', // ”
  '\u201E': '"', // „
  '\u2018': "'", // ‘
  '\u2019': "'", // ’
  '\u201A': "'", // ‚
};
export function normaliseSmartQuotes(input: string): string {
  return input.replace(
    /[\u201C\u201D\u201E\u2018\u2019\u201A]/g,
    (c) => SMART_QUOTE_MAP[c] || c,
  );
}

// A field whose value is free-form text (e.g. `supplier_name`) — i.e.
// NOT a single-token numeric / date / enum / uuid field. Such values
// legitimately contain spaces ("Acme Corp Ltd"), so a value pasted after
// such a field's prefix is auto-quoted by the Dashboard search box
// (OBO-46). Resolves aliases via `getFieldMeta` (`vendor_name` ->
// `supplier_name`) and recognises dynamic custom doc-type string fields,
// which live in `_CUSTOM_STRING_FIELD_NAMES` rather than FIELD_CATALOG.
export function isMultiTokenStringField(name: string): boolean {
  if (_CUSTOM_STRING_FIELD_NAMES.has(name.toLowerCase())) return true;
  const meta = getFieldMeta(name);
  return !!meta && meta.valueType === 'string';
}

// ── Field prefixes #/@/$ (Phase C-13) ────────────────────────────────
// Twitter-style shortcuts for the most common searches. Applied early
// so a later expansion doesn't mistake them for something else.
//
//   #INV           → invoice_id:INV        (substring match — finds INV-1234)
//   #INV-1234      → invoice_id:INV-1234   (substring match — still finds exact)
//   @Daniel        → assigned_to:Daniel    (name substring — typed path)
//   $5000+         → total_amount>=5000
//   $5k+ / $10M+   → 5000 / 10000000 (currency suffix runs after this
//                    so $5k → total_amount>=5k → total_amount>=5000)
//
// Anchored at word boundaries so `user#123` (hypothetical URL) doesn't
// get rewritten mid-token. Both `#` and `@` use `:` (match_phrase /
// substring) so partial tokens match — `#INV` finds `INV-1234`, and
// `@Dan` finds "Daniel Smith". The autocomplete picker inserts
// `assigned_to=<user_id>` directly (exact ID match) — this expansion
// only runs when the user types and submits without picking.
const HASH_PREFIX_RE = /(^|\s)#([A-Za-z0-9][A-Za-z0-9._\-]*)/g;
const AT_PREFIX_RE =
  /(^|\s)@([A-Za-z][A-Za-z0-9._\- ]*?)(?=\s+(?:AND|OR)\b|\s*$)/gi;
const DOLLAR_PREFIX_RE = /(^|\s)\$(\d+(?:[.,]\d+)?[kKmM]?)(\+?)/g;
export function expandFieldPrefixes(input: string): string {
  let out = input.replace(
    HASH_PREFIX_RE,
    (_m, pre, val) => `${pre}invoice_id:${val}`,
  );
  out = out.replace(AT_PREFIX_RE, (_m, pre, val) => {
    return `${pre}assigned_to:"${val.trim()}"`;
  });
  out = out.replace(DOLLAR_PREFIX_RE, (_m, pre, num, plus) => {
    const op = plus ? '>=' : '=';
    return `${pre}total_amount${op}${num}`;
  });
  return out;
}

// ── Operator aliases gt/gte/lt/lte/eq/ne (Phase A-1) ─────────────────
// Matches ` field <alias> value` where value is a quoted string or a
// bare word (no whitespace, no operator chars). Replaces with the
// symbolic operator the main parser already understands.
const OP_ALIAS_MAP: Record<string, string> = {
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  eq: '=',
  ne: '!=',
};
const OP_ALIAS_RE =
  /\b([a-zA-Z_][a-zA-Z0-9_]*)\s+(gt|gte|lt|lte|eq|ne)\s+("[^"]*"|'[^']*'|[^\s"'<>=!:]+)/gi;
export function expandOperatorAliases(input: string): string {
  return input.replace(OP_ALIAS_RE, (_m, field, alias, value) => {
    const sym = OP_ALIAS_MAP[alias.toLowerCase()];
    return `${field}${sym}${value}`;
  });
}

// ── Status shortcuts (Phase A-5) ─────────────────────────────────────
// Friendly labels map to backend enum values. Applied only when the
// user literally uses `status:<label>` or `status=<label>` with one of
// the known short words — other `status=...` queries pass through.
const STATUS_LABEL_MAP: Record<string, string> = {
  open: 'ready_for_validation',
  validation: 'ready_for_validation',
  closed: 'finished',
  done: 'finished',
  failed: 'error',
  broken: 'error',
};
const STATUS_SHORTCUT_RE = /\bstatus\s*[:=]\s*([a-zA-Z_]+)/gi;
export function expandStatusShortcuts(input: string): string {
  return input.replace(STATUS_SHORTCUT_RE, (m, label) => {
    const mapped = STATUS_LABEL_MAP[label.toLowerCase()];
    return mapped ? `status=${mapped}` : m;
  });
}

// ── Currency suffix 5k / 10M (Phase B-11) ────────────────────────────
// Matches a numeric value followed by `k` (×1 000) or `m`/`M`
// (×1 000 000). Only applies in obvious numeric contexts (after
// operator or after a currency prefix) so `1234 abc` is untouched.
const CURRENCY_SUFFIX_RE = /([<>=!]=?|\b)(\d+(?:[.,]\d+)?)([kKmM])\b/g;
export function expandCurrencySuffixes(input: string): string {
  return input.replace(CURRENCY_SUFFIX_RE, (_m, pre, num, suffix) => {
    const base = parseFloat(num.replace(',', '.'));
    const mult = suffix.toLowerCase() === 'k' ? 1_000 : 1_000_000;
    const expanded = (base * mult).toString();
    return `${pre}${expanded}`;
  });
}

// ── Overdue shortcut (Phase B-12) ────────────────────────────────────
// Domain-specific: "invoices whose due date already passed and are not
// yet finished". Emits two predicates joined by AND.
const OVERDUE_RE = /\boverdue\b/gi;
export function expandOverdueShortcut(input: string): string {
  return input.replace(
    OVERDUE_RE,
    'invoice_due_date<today() AND status!=finished',
  );
}

// ── Bare date keywords (Phase A-2) ───────────────────────────────────
// `today` / `yesterday` / `tomorrow` WITHOUT parentheses. Expanded to
// today() / today()-1 / today()+1 so the existing expandRelativeDate
// in the main parser turns them into ISO strings.
const BARE_DATE_RE = /\b(today|yesterday|tomorrow)\b(?!\s*\()/gi;
const BARE_DATE_MAP: Record<string, string> = {
  today: 'today()',
  yesterday: 'today()-1',
  tomorrow: 'today()+1',
};
export function expandBareDateKeywords(input: string): string {
  return input.replace(BARE_DATE_RE, (_m, word) => {
    return BARE_DATE_MAP[word.toLowerCase()];
  });
}

// ── Date literal normalisation ───────────────────────────────────────
// User-typed date formats → canonical `yyyy-mm-dd`. Accepts the four
// common European/US shapes; 4-digit-year anchor avoids false-positive
// matches on version strings like `1.2.3` or path fragments.
//
//   22.03.2026 → 2026-03-22   (de)
//   22/03/2026 → 2026-03-22   (fr/it/es)
//   22-03-2026 → 2026-03-22   (nl)
//   2026.03.22 → 2026-03-22   (some EU formats)
//   2026/03/22 → 2026-03-22   (us-locale long)
//   2026-03-22 → 2026-03-22   (already ISO, pass-through)
const DMY_DATE_RE = /\b(\d{1,2})[./-](\d{1,2})[./-](\d{4})\b/g;
const YMD_DATE_RE = /\b(\d{4})[./](\d{1,2})[./](\d{1,2})\b/g;
const pad2 = (s: string): string => (s.length === 1 ? '0' + s : s);
export function normaliseDateLiterals(input: string): string {
  let out = input.replace(
    YMD_DATE_RE,
    (_m, y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`,
  );
  out = out.replace(DMY_DATE_RE, (_m, d, m, y) => `${y}-${pad2(m)}-${pad2(d)}`);
  return out;
}

// ── Period shortcuts (Phase A-3) ─────────────────────────────────────
// `field this_week` / `last_week` / `this_month` / `last_month` /
// `this_year` / `last_year` → two predicates covering the window.
// Uses ISO week (Mon-Sun).
function periodBounds(key: string, ref: Date = new Date()): [string, string] {
  const d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  const iso = (x: Date): string => {
    const y = x.getFullYear();
    const m = String(x.getMonth() + 1).padStart(2, '0');
    const dd = String(x.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };
  switch (key) {
    case 'this_week': {
      const day = d.getDay() || 7; // Sun=7
      const mon = new Date(d);
      mon.setDate(d.getDate() - (day - 1));
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      return [iso(mon), iso(sun)];
    }
    case 'last_week': {
      const day = d.getDay() || 7;
      const mon = new Date(d);
      mon.setDate(d.getDate() - (day - 1) - 7);
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      return [iso(mon), iso(sun)];
    }
    case 'next_week': {
      const day = d.getDay() || 7;
      const mon = new Date(d);
      mon.setDate(d.getDate() - (day - 1) + 7);
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      return [iso(mon), iso(sun)];
    }
    case 'this_month': {
      const first = new Date(d.getFullYear(), d.getMonth(), 1);
      const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      return [iso(first), iso(last)];
    }
    case 'last_month': {
      const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const last = new Date(d.getFullYear(), d.getMonth(), 0);
      return [iso(first), iso(last)];
    }
    case 'next_month': {
      const first = new Date(d.getFullYear(), d.getMonth() + 1, 1);
      const last = new Date(d.getFullYear(), d.getMonth() + 2, 0);
      return [iso(first), iso(last)];
    }
    case 'this_year':
      return [
        iso(new Date(d.getFullYear(), 0, 1)),
        iso(new Date(d.getFullYear(), 11, 31)),
      ];
    case 'last_year':
      return [
        iso(new Date(d.getFullYear() - 1, 0, 1)),
        iso(new Date(d.getFullYear() - 1, 11, 31)),
      ];
    default:
      return [iso(d), iso(d)];
  }
}
const PERIOD_RE =
  /\b([a-zA-Z_][a-zA-Z0-9_]*)\s+(this_week|last_week|next_week|this_month|last_month|next_month|this_year|last_year)\b/gi;
export function expandPeriodShortcuts(input: string, now?: Date): string {
  return input.replace(PERIOD_RE, (_m, field, key) => {
    const [lo, hi] = periodBounds(key.toLowerCase(), now);
    return `${field}>=${lo} AND ${field}<=${hi}`;
  });
}

// ── Presence words (Phase A-4) ───────────────────────────────────────
// `is empty` / `is not empty` / `is set` → value `""` with `=` or `!=`.
//
// Bug-2026-04-28: extended to accept the human-readable display label
// (e.g. "Invoice Due Date is not null") in addition to the snake_case
// canonical name ("invoice_due_date is not null"). The chip-editor
// renders the label in the dropdown / chip body, so users coming from
// the chip view to raw mode reasonably try to type what they saw.
//
// The regex captures a permissive multi-word field portion and the
// replace callback resolves it back to the canonical name via a
// label→name map derived from FIELD_CATALOG. Unknown labels are left
// intact so we don't accidentally rewrite arbitrary prose like
// `colleague is null` (no field named "colleague" → no rewrite).
// The capture builds a multi-word field name by appending whitespace-
// separated words, but each appended word must NOT be a logical
// connector (AND / OR / NOT) or the literal `is` that opens the
// presence clause. Without that per-word guard `filename:PO206 AND
// Invoice Due Date is not null` would match starting at `PO206` and
// span across the `AND` connector, producing the bogus capture
// "PO206 AND Invoice Due Date" — which the FIELD_CATALOG lookup
// then fails on, leaving the input untouched. The leading negative
// lookahead also blocks AND/OR/NOT as the FIRST word of the field
// (so the engine moves past the connector itself and starts a fresh
// match at the next field name).
const PRESENCE_RE =
  /\b(?!(?:AND|OR|NOT)\b)([a-zA-Z_][a-zA-Z0-9_]*(?:[ ]+(?!(?:AND|OR|NOT|is)\b)[a-zA-Z_][a-zA-Z0-9_]*)*)\s+is\s+(not\s+)?(empty|set|null|missing)\b/gi;

// Convert snake_case to Title Case for the auto-generated display label
// fallback when a catalog entry has no explicit `displayName`.
function _snakeToTitle(name: string): string {
  return name
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');
}

// Lazy-built lower-cased label → canonical name map. Built on first use
// so we don't churn through FIELD_CATALOG per call. The same map is
// used for the snake_case form (which is its own label) so the regex
// can fire on either input shape.
let _LABEL_TO_NAME: Map<string, string> | null = null;
function _labelToName(): Map<string, string> {
  if (_LABEL_TO_NAME) return _LABEL_TO_NAME;
  const m = new Map<string, string>();
  for (const meta of FIELD_CATALOG) {
    // Self-mapping: `invoice_due_date` → `invoice_due_date` so the
    // legacy snake_case path still hits the same code path.
    m.set(meta.name.toLowerCase(), meta.name);
    // Display form (explicit when set, otherwise auto-generated from
    // the canonical name).
    const displayName = meta.displayName ?? _snakeToTitle(meta.name);
    if (displayName) m.set(displayName.toLowerCase(), meta.name);
  }
  _LABEL_TO_NAME = m;
  return m;
}

export function expandPresenceWords(input: string): string {
  return input.replace(PRESENCE_RE, (match, field, neg, word) => {
    const cleaned = String(field).trim();
    const canonical = _labelToName().get(cleaned.toLowerCase());
    if (!canonical) {
      // Unknown label — leave the original text untouched so we don't
      // mangle arbitrary prose. Returning `match` keeps the caller's
      // input verbatim.
      return match;
    }
    const wordLower = word.toLowerCase();
    const isSetWord = wordLower === 'set';
    // "is empty" / "is null" / "is missing" → ="" ;  "is set" → !=""
    // "is not empty/null/missing" → !=""      ;  "is not set" → =""
    const empty = neg ? isSetWord : !isSetWord;
    return empty ? `${canonical}=""` : `${canonical}!=""`;
  });
}

// ── String operators contains/starts_with/ends_with (Phase B-10) ─────
// contains → same as `:` (substring). starts_with / ends_with get
// encoded as a phrase with a positional marker that quick-search.vue
// could later interpret for native ILIKE 'X%' / '%X' — for now they
// fall back to substring so functional parity is kept.
const STRING_OP_RE =
  /\b([a-zA-Z_][a-zA-Z0-9_]*)\s+(contains|starts_with|ends_with)\s+("[^"]*"|'[^']*'|\S+)/gi;
export function expandStringOperators(input: string): string {
  return input.replace(STRING_OP_RE, (_m, field, op, value) => {
    // All three variants ride the `:` (substring) operator on the
    // backend; the distinction is mostly a documentation affordance.
    const opLower = op.toLowerCase();
    if (opLower === 'starts_with' || opLower === 'ends_with') {
      // Same substring match — TODO: switch to native LIKE anchors
      // when the backend grows explicit prefix/suffix ops.
      return `${field}:${value}`;
    }
    return `${field}:${value}`;
  });
}

// ── IN list (Phase B-7) ──────────────────────────────────────────────
// `field in (a, b, c)` → `field=a OR field=b OR field=c`
// `field not in (a, b, c)` → `field!=a AND field!=b AND field!=c`
const IN_LIST_RE =
  /\b([a-zA-Z_][a-zA-Z0-9_]*)\s+(not\s+)?in\s*\(\s*([^)]+?)\s*\)/gi;
export function expandInList(input: string): string {
  return input.replace(IN_LIST_RE, (_m, field, neg, list) => {
    // Split on commas, then trim and preserve quotes around each part.
    const parts = list
      .split(',')
      .map((x: string) => x.trim())
      .filter(Boolean);
    if (parts.length === 0) return '';
    const op = neg ? '!=' : '=';
    const connector = neg ? 'AND' : 'OR';
    return parts.map((p: string) => `${field}${op}${p}`).join(` ${connector} `);
  });
}

// ── Negation prefix (Phase B-9) ──────────────────────────────────────
// `not field=X` → `field!=X`,  `not field:X` → `field!=X`,
// `not field>X` → `field<=X`,  `not field>=X` → `field<X`,
// `not field<X` → `field>=X`,  `not field<=X` → `field>X`.
const NOT_OP_INVERSION: Record<string, string> = {
  '=': '!=',
  ':': '!=',
  '!=': '=',
  '>': '<=',
  '>=': '<',
  '<': '>=',
  '<=': '>',
};
const NEGATION_RE =
  /\bnot\s+([a-zA-Z_][a-zA-Z0-9_]*)(>=|<=|!=|=|:|>|<)("[^"]*"|'[^']*'|\S+)/gi;
export function expandNegationPrefix(input: string): string {
  return input.replace(NEGATION_RE, (_m, field, op, value) => {
    const inverse = NOT_OP_INVERSION[op] || op;
    return `${field}${inverse}${value}`;
  });
}

// ── NOT BETWEEN (Phase A-6) ──────────────────────────────────────────
// `field not between X and Y` → `(field<X OR field>Y)`
// Bug-2026-05-25 #7/#17/#21/#31/#37/#38/#39/#41: same negative-
// lookbehind value-position guard as BETWEEN_RE.
const NOT_BETWEEN_RE =
  /(?<![:=<>!][\s(]*)\b([a-zA-Z_][a-zA-Z0-9_]*)\s+not\s+between\s+("[^"]*"|'[^']*'|[^\s,]+)\s*(?:,|\s+and\s+)\s*("[^"]*"|'[^']*'|\S+)/gi;
export function expandNotBetween(input: string): string {
  // Bug-2026-05-25 #9 + #12: same dual-guard layer as expandBetween —
  // semantic-prefix skip + quote-mask + field-validator.
  return withQuotesAndPrefixesProtected(input, (masked) =>
    masked.replace(NOT_BETWEEN_RE, (match, field, lo, hi) => {
      if (!isSupportedSearchField(field.toLowerCase())) {
        return match;
      }
      // Bug #40 (Codex v7): the OR MUST be grouped — otherwise
      // `status=active AND imported_on not between A and B` becomes
      // `status=active AND imported_on<A OR imported_on>B`, and the
      // OR escapes the conjunction, returning docs outside the
      // intended `status=active` filter.
      return `(${field}<${lo} OR ${field}>${hi})`;
    }),
  );
}

// ── Sort suffix (Phase C-16) ─────────────────────────────────────────
// `... sort <field> asc|desc` at the END of the query lifts the
// directive out into `sortOverride` so quick-search.vue can apply it
// to the dashboard's order_by_field / order_by_direction. Default
// direction is `desc` (matches the dashboard's default).
const SORT_SUFFIX_RE =
  /\s+sort\s+([a-zA-Z_][a-zA-Z0-9_]*)(?:\s+(asc|desc))?\s*$/i;
export function extractSortSuffix(input: string): {
  stripped: string;
  sortOverride?: { field: string; direction: 'asc' | 'desc' };
} {
  const m = input.match(SORT_SUFFIX_RE);
  if (!m) return { stripped: input };
  const stripped = input.replace(SORT_SUFFIX_RE, '').trim();
  return {
    stripped,
    sortOverride: {
      field: m[1].toLowerCase(),
      direction: (m[2] || 'desc').toLowerCase() as 'asc' | 'desc',
    },
  };
}

// ── Group-by directive ──────────────────────────────────────────────
// `group by <field> [day|week|month]` is recognised at the END or
// BEGINNING of the query. The directive is lifted out into
// `groupByOverride` so quick-search.vue can forward it to
// dashboard.vue's selectedGroupBy state without polluting the
// predicate stream. Field aliases are accepted: `vendor` → supplier_name,
// `invoice` → invoice_date so the natural-language form
// `group by vendor` works without forcing the user to know the
// canonical column name.
const GROUP_BY_FIELD_ALIAS: Record<string, string> = {
  vendor: 'supplier_name',
  // Full-form procurement term (`vendor_name`) — surfaces from typed
  // queries AND from the autocomplete dropdown when the user picks
  // "Vendor Name" while in directive mode (the FIELD_CATALOG entry
  // for vendor_name carries that label). Both paths must canonicalise
  // to the actual ClickHouse column `supplier_name`.
  vendor_name: 'supplier_name',
  supplier: 'supplier_name',
  invoice: 'invoice_date',
  created: 'created_on',
  delivery: 'delivery_date',
  type: 'doc_type',
  doctype: 'doc_type',
  assignee: 'assigned_to',
};
const GROUP_BY_SUFFIX_RE =
  /(?:^|\s+)group\s+by\s+([a-zA-Z_][a-zA-Z0-9_]*)(?:\s+(day|week|month))?\s*$/i;
export function extractGroupBy(input: string): {
  stripped: string;
  groupByOverride?: { field: string; bucket?: 'day' | 'week' | 'month' };
} {
  const m = input.match(GROUP_BY_SUFFIX_RE);
  if (!m) return { stripped: input };
  const stripped = input.replace(GROUP_BY_SUFFIX_RE, '').trim();
  const rawField = m[1].toLowerCase();
  const field = GROUP_BY_FIELD_ALIAS[rawField] ?? rawField;
  const bucket = m[2]?.toLowerCase() as 'day' | 'week' | 'month' | undefined;
  return {
    stripped,
    groupByOverride: bucket ? { field, bucket } : { field },
  };
}

// DOCB-12249 (Hanna's comment #86606 follow-up): the group-by directive
// is now persisted as part of `quickFilterMap.raw_query` so the
// QuickSearch chip editor can rehydrate the visual `Gruppieren nach :`
// chip on every Dashboard ↔ /field_validation_v1 round-trip. The
// backend, however, takes the group axis through the dedicated
// `group_by` slot and would 422 on a trailing `group by …` token in
// raw_query. `makeQueryParamFromFilters()` in dashboard.store.ts calls
// this helper to strip the directive at the HTTP wire boundary —
// exactly once, in one place, instead of pre-stripping at storage time.
export function stripGroupByDirective(input: string): string {
  return input.replace(GROUP_BY_SUFFIX_RE, '').trim();
}

// DOCB-12249 (Codex review Round 2 LOW): co-located with
// `stripGroupByDirective` so callers that need to know "does this raw
// query already carry a directive?" share the same regex. Used by
// `dashboard.store.selectDashboard()` to skip the synthesise-from-
// customizations path when the saved raw_query already ends with a
// directive (post-fix dashboards).
export function hasGroupByDirective(input: string): boolean {
  return GROUP_BY_SUFFIX_RE.test(input);
}

export function parseSearchQuery(raw: string): ParsedSearch {
  // Convenience-syntax expansion pipeline. Order is load-bearing:
  //   1. Smart quotes first — so later regexes see straight quotes.
  //   2. Field prefixes (#/@/$) before operator aliases/currency —
  //      otherwise $5k+ would be mangled by the currency expansion.
  //   3. Operator aliases next so later features can use bare-word
  //      values without clashing with `gt`/`lt`.
  //   4. Status shortcuts before `in (…)` — `status:open` stays a
  //      simple = predicate instead of a list.
  //   5. Currency suffixes & overdue before date expansions.
  //   6. Bare-date (`today`) before period (`this_week`) so the
  //      period regex doesn't accidentally eat `today`.
  //   7. Presence/string/in/negation — all value-shape transforms.
  //   8. Between variants last — they build multi-predicate output.
  //   9. Sort suffix extraction is pulled off BEFORE the main parse
  //      so `... sort X asc` never ends up in the predicate stream.
  let trimmed = (raw || '').trim();
  trimmed = normaliseSmartQuotes(trimmed);
  trimmed = expandFieldPrefixes(trimmed);
  trimmed = expandOperatorAliases(trimmed);
  trimmed = expandStatusShortcuts(trimmed);
  trimmed = expandCurrencySuffixes(trimmed);
  trimmed = expandOverdueShortcut(trimmed);
  trimmed = expandBareDateKeywords(trimmed);
  trimmed = normaliseDateLiterals(trimmed);
  trimmed = expandPeriodShortcuts(trimmed);
  trimmed = expandPresenceWords(trimmed);
  trimmed = expandStringOperators(trimmed);
  trimmed = expandInList(trimmed);
  trimmed = expandNegationPrefix(trimmed);
  trimmed = expandNotBetween(trimmed);
  trimmed = expandBetween(trimmed);
  // Run extractGroupBy BEFORE extractSortSuffix: both anchor on
  // end-of-string, but if the user types `... sort X asc group by Y`,
  // the group-by directive sits at the tail, so it must be peeled off
  // first so the sort-suffix matcher can then see its own anchor.
  const gb = extractGroupBy(trimmed);
  const groupByOverride = gb.groupByOverride;
  trimmed = gb.stripped;
  const { stripped, sortOverride } = extractSortSuffix(trimmed);
  trimmed = stripped;
  if (!trimmed) {
    return {
      predicates: [],
      keyword: '',
      requiresFulltext: false,
      isVectorMode: false,
      isAiMode: false,
      isOcrMode: false,
      ...(sortOverride ? { sortOverride } : {}),
      ...(groupByOverride ? { groupByOverride } : {}),
    };
  }

  // Detect `ocr:` before vector. It is explicit fulltext over OCR/table/text
  // companion content, not semantic kNN and not the AI parser.
  const isOcrMode = OCR_PREFIX_RE.test(trimmed);
  if (isOcrMode) {
    return {
      predicates: [],
      keyword: trimmed.replace(OCR_PREFIX_RE, '').trim(),
      requiresFulltext: true,
      isVectorMode: false,
      isAiMode: false,
      isOcrMode: true,
      ...(sortOverride ? { sortOverride } : {}),
      ...(groupByOverride ? { groupByOverride } : {}),
    };
  }

  // Detect `ai:` prefix FIRST (more specific than the generic vector
  // detector below). `ai:` routes to fulltextsearch's natural-language
  // query parser (Ollama LLM), NOT to kNN — keep it isolated.
  const isAiMode = AI_PREFIX_RE.test(trimmed);
  if (isAiMode) {
    return {
      predicates: [],
      keyword: trimmed.replace(AI_PREFIX_RE, '').trim(),
      requiresFulltext: true, // ai needs fulltextsearch
      isVectorMode: false,
      isAiMode: true,
      isOcrMode: false,
      ...(sortOverride ? { sortOverride } : {}),
      ...(groupByOverride ? { groupByOverride } : {}),
    };
  }

  // Detect and strip the vector: prefix. Everything after it is a
  // free-text kNN semantic query.
  const isVectorMode = VECTOR_PREFIX_RE.test(trimmed);
  if (isVectorMode) {
    return {
      predicates: [],
      keyword: trimmed.replace(VECTOR_PREFIX_RE, '').trim(),
      requiresFulltext: true, // vector needs OpenSearch
      isVectorMode: true,
      isAiMode: false,
      isOcrMode: false,
      ...(sortOverride ? { sortOverride } : {}),
      ...(groupByOverride ? { groupByOverride } : {}),
    };
  }

  // Bare-UUID shortcut: when the user pastes a document UUID without
  // any operator, treat it as `id=<UUID>` so `quick-search.vue` takes
  // the Postgres fast-path (idPred.value → keyword) instead of shipping
  // the raw UUID as a substring search that returns zero hits.
  if (UUID_RE.test(trimmed)) {
    return {
      predicates: [
        {
          field: 'id',
          operator: '=',
          value: trimmed,
        },
      ],
      keyword: '',
      requiresFulltext: false,
      isVectorMode: false,
      isAiMode: false,
      isOcrMode: false,
      ...(sortOverride ? { sortOverride } : {}),
      ...(groupByOverride ? { groupByOverride } : {}),
    };
  }

  // Multi-token input with operators may appear as three tokens
  // (field, op, value). Join those back before tokenising so
  // `total_amount > 5000` parses the same as `total_amount>5000`.
  const collapsed = trimmed.replace(/\s*(>=|<=|!=|>|<|=|:)\s*/g, '$1');

  const tokens = tokenise(collapsed);
  const predicates: ParsedPredicate[] = [];
  const keywordTokens: string[] = [];
  let requiresFulltext = false;
  let pendingConnector: LogicalConnector | undefined = undefined;

  for (const tok of tokens) {
    // Parens are lexical grouping markers that the raw query string
    // carries verbatim to the backend. The flat predicate list below
    // can't represent grouping — that's OK because the backend is
    // expected to prefer the raw_query field for any query containing
    // parens. Skip them here so they don't pollute the keyword path.
    if (tok === '(' || tok === ')') continue;
    const upper = tok.toUpperCase();
    if (upper === 'AND' || upper === 'OR') {
      pendingConnector = upper as LogicalConnector;
      continue;
    }
    const match = PREDICATE_RE.exec(tok);
    if (!match) {
      keywordTokens.push(tok);
      // If there's a leading connector but no predicate consumes it,
      // don't carry it — avoids an AND silently attaching to the next
      // predicate far away from what the user typed.
      pendingConnector = undefined;
      continue;
    }
    const [, field, op, valueRaw] = match;
    const fieldLower = field.toLowerCase();
    if (!isSupportedSearchField(fieldLower)) {
      keywordTokens.push(tok);
      pendingConnector = undefined;
      continue;
    }
    // Record quoted-ness BEFORE stripping quotes — string fields use it to
    // switch `=` from STARTS-WITH to STARTS-WITH-OR-ENDS-WITH on the backend.
    const trimmedRaw = valueRaw.trim();
    const wasQuoted =
      (trimmedRaw.startsWith('"') && trimmedRaw.endsWith('"')) ||
      (trimmedRaw.startsWith("'") && trimmedRaw.endsWith("'"));
    const rawValue = unquote(valueRaw).trim();
    // Empty value is only meaningful with `=` / `!=` (presence check:
    // "field is empty" / "field is set"). For any other operator an
    // empty right-hand side is user error, so we drop the token back
    // into keyword as before.
    const isPresenceCheck = (op === '=' || op === '!=') && rawValue === '';
    if (!rawValue && !isPresenceCheck) {
      keywordTokens.push(tok);
      pendingConnector = undefined;
      continue;
    }
    // Predicates preserve the user's exact field name and value — no
    // FIELD_ALIASES rewrite, no `today()` → ISO expansion at this layer.
    // The chip editor and URL persistence both read this shape and must
    // round-trip what the user typed. Canonicalisation for the backend
    // happens in `canonicaliseForBackend` at the HTTP-serialisation edge.
    predicates.push({
      field: fieldLower,
      operator: op as SearchOperator,
      value: rawValue,
      // Anchoring intent only applies to `=` / `!=`. Quotes on `:` are just
      // phrase grouping (spaces), not a start/end-match signal.
      quoted: (op === '=' || op === '!=') && wasQuoted ? true : undefined,
      // First predicate has no connector; subsequent predicates
      // default to AND when the user didn't type one explicitly.
      connector:
        predicates.length === 0 ? undefined : pendingConnector || 'AND',
    });
    pendingConnector = undefined;

    // Bug 2026-05-05: extended-category fields (currently supplier_name)
    // resolve through Postgres `document_extracted_data` — every
    // operator (=, !=, :, presence, range) is filterable WITHOUT a
    // Fulltext license. Same for standard catalog fields, which already
    // have native PG columns / JOIN expressions in the routing layer
    // (assigned_to IS NOT NULL, created_on >= '2026-01-01', etc.).
    // The legacy rules below were correct when ALL extracted fields
    // needed OpenSearch — they're now too aggressive and produce a
    // misleading "Fulltext required" warning chip on queries that
    // actually work fine.
    const meta = getFieldMeta(fieldLower);
    const isStandard = meta?.category === 'standard';
    const isExtended =
      meta?.category === 'extended' || _EXTENDED_FIELD_NAMES.has(fieldLower);

    if (!isStandard && !isExtended) {
      if (isPresenceCheck) {
        // Presence query (exists / must_not exists) on a fulltext field
        // is an OpenSearch op.
        requiresFulltext = true;
      }
      if (op !== '=' && op !== ':') {
        requiresFulltext = true;
      }
      if (EXTRACTED_ONLY_FIELDS.has(fieldLower)) {
        requiresFulltext = true;
      }
    }
  }

  return {
    predicates,
    keyword: keywordTokens.join(' '),
    requiresFulltext,
    isVectorMode: false,
    isAiMode: false,
    isOcrMode: false,
    ...(sortOverride ? { sortOverride } : {}),
    ...(groupByOverride ? { groupByOverride } : {}),
  };
}

/**
 * Rewrite a raw query string into its backend-canonical form.
 *
 * Called ONLY at the HTTP-serialisation boundary (see dashboard.store.ts
 * where `raw_query` is URL-encoded). The chip editor, URL persistence,
 * and `parseSearchQuery.predicates` all deal in the user-typed form —
 * this step is what bridges the two worlds so the backend still sees
 * the canonical column names and concrete ISO dates it expects.
 *
 * Three transformations:
 *   0. Range sugar:    field between A and B → field>=A AND field<=B
 *                      field not between A and B → field<A OR field>B
 *   1. Field aliases:  imported_on → created_on
 *                      updated / updated_on → last_modified_on
 *   2. Relative dates: today() / today()-N / now() → YYYY-MM-DD
 *
 * Edge cases to keep in mind while implementing:
 *   - Field name must appear immediately before an operator (=, !=, >,
 *     <, >=, <=, :) — otherwise matching `updated` inside a free-text
 *     keyword (e.g. `updated invoices`) would wrongly rewrite it.
 *   - Quoted string values must pass through untouched (a user search
 *     for the literal string `"today()"` in an invoice line shouldn't
 *     get date-expanded). Values inside `"…"` / `'…'` are off-limits.
 *   - Relative-date literals appear only in predicate VALUE position —
 *     directly after a comparison operator. `expandRelativeDate` below
 *     does the actual ISO conversion; this function is about FINDING
 *     the right spans to apply it to.
 *   - Aliases are case-insensitive at the field position but the
 *     replacement is always lowercase canonical (`created_on`, etc.).
 */
export function canonicaliseForBackend(raw: string): string {
  if (!raw?.trim()) return raw;

  // Backend raw-query parsers historically understand comparison
  // predicates reliably; keep frontend-only range sugar out of the
  // wire payload so a readable chip still produces backend-canonical
  // predicates.
  raw = expandBetween(expandNotBetween(normaliseDateLiterals(raw)));

  // 1. Mask quoted spans so the alias + date regexes can't reach inside.
  //    A literal `filename:"today() imported_on"` must pass through.
  const masks: string[] = [];
  const QUOTED_RE = /"[^"]*"|'[^']*'/g;
  let out = raw.replace(QUOTED_RE, (m) => {
    const idx = masks.length;
    masks.push(m);
    return `\x00${idx}\x00`;
  });

  // 2. Field aliases: `<word>` immediately before an operator only.
  //    Lookahead keeps `updated invoices` (free-text) untouched.
  const ALIAS_FIELD_RE =
    /\b([a-zA-Z_][a-zA-Z0-9_]*)\b(?=\s*(?:>=|<=|!=|=|:|>|<))/g;
  out = out.replace(ALIAS_FIELD_RE, (m, field) => {
    const canonical = FIELD_ALIASES[field.toLowerCase()];
    return canonical ?? m;
  });

  // 3. Relative-date literals: only in value position (after an operator).
  //    `expandRelativeDate` handles today() / today()-N / now() itself.
  const VALUE_RELDATE_RE =
    /(>=|<=|!=|=|:|>|<)\s*((?:today|now)\(\)(?:[+-]\d+)?)/gi;
  out = out.replace(
    VALUE_RELDATE_RE,
    (_m, op, rel) => op + expandRelativeDate(rel),
  );

  // Bug-2026-05-25 #58 (Codex v11 [high]): the doc2-api SQL/fulltext
  // splitter only descends top-level AND children — a nested AND
  // group hides SQL-only fields behind a fulltext-only routing,
  // which can drop the SQL-side range constraint from a mixed
  // query like `total_amount>100 AND imported_on between A and B`.
  // My v7 #40/#41 paren-wrapping of `expandBetween` output made
  // every positive BETWEEN nested.  Strip those specific groups at
  // the HTTP boundary — `(field op val AND field op val)` where
  // both operands share the field — UNLESS preceded by `NOT` (where
  // the parens are load-bearing for negation scope, #41).
  //
  // `expandNotBetween`'s OR-shape `(a<X OR a>Y)` is NOT stripped
  // (the regex requires the inner connector be `AND`), so #40's
  // grouping survives.  User-typed parens around OR groups also
  // survive (same `AND`-only filter).
  // Bug-2026-05-25 #59 (Codex v12 [high]): the NOT-guard must tolerate
  // ANY trailing whitespace after `NOT`, not just one space.  Pre-#59
  // `NOT  imported_on between A and B` (two spaces) defeated
  // `(?<!\bnot\s)` and the parens were stripped, breaking negation
  // scope.  Use the replace callback to inspect the prefix manually
  // — variable-length lookbehind would work too, but the callback is
  // more robust across JS engines and easier to read.
  const POS_BETWEEN_GROUP_RE =
    /\(([a-zA-Z_][a-zA-Z0-9_]*)\s*(>=|<=)\s*(\S+?)\s+AND\s+\1\s*(>=|<=)\s*([^\s)]+)\)/gi;
  out = out.replace(
    POS_BETWEEN_GROUP_RE,
    (match, field, op1, v1, op2, v2, offset: number, full: string) => {
      // Preserve the parens when the group is preceded by NOT (with
      // ANY whitespace run between).  `/\bnot\s+$/i` against the
      // prefix is the variable-whitespace form of the v11 lookbehind.
      const prefix = full.slice(0, offset);
      if (/\bnot\s+$/i.test(prefix)) return match;
      return `${field}${op1}${v1} AND ${field}${op2}${v2}`;
    },
  );

  // 4. Restore quoted spans verbatim.
  // eslint-disable-next-line no-control-regex
  out = out.replace(/\x00(\d+)\x00/g, (_m, idx) => masks[Number(idx)]);

  return out;
}

// ---------------------------------------------------------------------------
// Catalog hydration — replaces the hardcoded FIELD_CATALOG with the backend
// version at app startup. Backend file:
// doc2-api/routers/document_process/search_fields_catalog.py
//
// The backend ships display_name / supports_presence in snake_case; we map
// them to the camelCase TS shape. Unknown extra keys from the backend pass
// through harmlessly — the FieldMeta type ignores them.
// ---------------------------------------------------------------------------

export interface BackendFieldEntry {
  name: string;
  category: string;
  ops: string[];
  value_type?: string;
  display_name?: string;
  supports_presence?: boolean;
  description?: string;
  description_key?: string;
  label_key?: string;
  example?: string;
}

function _augmentOpsWithNeq(
  ops: string[],
  valueType: FieldMeta['valueType'],
): string[] {
  if (ops.includes('!=')) return ops;
  if (!ops.includes('=')) return ops;
  if (valueType === 'date') return ops;
  const equalsIndex = ops.indexOf('=');
  const next = [...ops];
  next.splice(equalsIndex + 1, 0, '!=');
  return next;
}

function _backendToFieldMeta(entry: BackendFieldEntry): FieldMeta | null {
  if (!entry || typeof entry.name !== 'string' || !entry.name) return null;
  if (!Array.isArray(entry.ops) || entry.ops.length === 0) return null;
  const valueType = (entry.value_type ?? 'string') as FieldMeta['valueType'];
  return {
    name: entry.name,
    category: entry.category as FieldMeta['category'],
    ops: _augmentOpsWithNeq(
      entry.ops,
      valueType,
    ) as unknown as FieldMeta['ops'],
    valueType,
    displayName: entry.display_name,
    description: entry.description ?? '',
    descriptionKey: entry.description_key,
    labelKey: entry.label_key ?? entry.name,
    example: entry.example,
    supportsPresence: entry.supports_presence,
  };
}

/**
 * Replace FIELD_CATALOG contents with the backend's canonical list.
 * Called once at app startup by `loadCatalogFromBackend()`. Mutates the
 * exported array in-place so existing imports (FIELD_CATALOG, the
 * autocomplete component, the chip editor, the parser) all see the new
 * fields without needing reactive plumbing.
 *
 * Failure modes are silent: if the API is down, returns false and the
 * frontend keeps the shipped fallback. Worst case: a field added on the
 * backend last week isn't yet in the autocomplete. The user can still
 * type it manually and the backend will route it correctly.
 */
export function hydrateFieldCatalog(
  backendFields: ReadonlyArray<BackendFieldEntry>,
): boolean {
  if (!Array.isArray(backendFields) || backendFields.length === 0) return false;
  const next: FieldMeta[] = [];
  for (const raw of backendFields) {
    const meta = _backendToFieldMeta(raw);
    if (meta) next.push(meta);
  }
  if (!next.length) return false;
  // Mutate in place so all import-time array references stay valid.
  FIELD_CATALOG.splice(0, FIELD_CATALOG.length, ...next);
  // Invalidate caches that derive from FIELD_CATALOG.
  _LABEL_TO_NAME = null;
  return true;
}

// ---------------------------------------------------------------------------
// Bare-text auto-promotion (DOCB-12063, revised 2026-05-18)
//
// Mirrors the existing bare-UUID shortcut at parseSearchQuery line ~1717:
// when the user types pure free text (no field predicates, no prefix
// modes), the wire payload should reflect a fulltext-keyword search
// across ALL fields.
//
// 2026-05-18 user-reported bug fix: an earlier revision auto-promoted
// bare text to `isVectorMode=true` for vector-enabled orgs (e.g. CROSS),
// and to a `filename:` predicate for everyone else. Both behaviors are
// wrong per the explicit product rule:
//
//   "ohne ein extra ist immer auf alles felder"
//     — bare text always searches across all fields = fulltext keyword.
//
//   "vector ist nur wenn man nach vector oder semantic sucht sonst nie"
//     — semantic/vector search ONLY when the user explicitly types the
//       `vector:` or `semantic:` prefix.
//
// So this function now keeps bare text exactly as the parser produced
// it (as a `keyword`, no predicate, no vector promotion) and just sets
// `requiresFulltext=true` so downstream code routes it as a fulltext
// keyword search. `parseSearchQuery` line 1725 sets `isVectorMode=true`
// only when the explicit `vector:`/`semantic:` prefix is present —
// that remains the sole entry point for semantic search.
//
// `capabilities` is kept in the signature for backwards-compatibility
// with the call sites (and to leave room for future per-org gates),
// but its `vectorEnabled` field is no longer consulted for bare text:
// the rule is the same on every org.
// ---------------------------------------------------------------------------

export interface PromoteCapabilities {
  vectorEnabled: boolean;
  fulltextEnabled?: boolean;
}

export function promoteBareKeyword(
  parsed: ParsedSearch,
  capabilities: PromoteCapabilities,
): ParsedSearch {
  if (parsed.predicates.length > 0) return parsed;
  if (parsed.isVectorMode || parsed.isAiMode || parsed.isOcrMode) return parsed;
  const text = parsed.keyword.trim();
  if (text.length === 0) return parsed;

  const fulltextEnabled = capabilities.fulltextEnabled ?? true;
  return {
    ...parsed,
    keyword: text,
    isVectorMode: false,
    requiresFulltext: fulltextEnabled,
  };
}
